'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';
import LayoutDefault from '../../layouts/LayoutDefault';

// Dynamic import CKEditor
const CKEditor = dynamic(() => import('@/components/CKEditor/CKEditorWrapper'), {
  ssr: false,
  loading: () => <div className="animate-pulse bg-gray-200 h-96 rounded"></div>
});

// Dynamic import ModelPreviewModal
const ModelPreviewModal = dynamic(() => import('@/components/ModelPreviewModal/ModelPreviewModal'), {
  ssr: false,
});

// Dynamic import MediaPreviewModal
const MediaPreviewModal = dynamic(() => import('../resourceManagement/components/MediaPreviewModal'), {
  ssr: false,
});

import FileUploader from './fileUploader';
import {
  SearchIcon,
  AddIcon,
  EditIcon,
  DuplicateIcon,
  TrashIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowDownIcon,
  FolderIcon,
  SmallFolderIcon,
  VideoIcon,
  ImageIcon,
  AudioIcon,
  Object3DIcon,
  DocumentIcon,
  FlipBookIcon,
  LargeVideoIcon,
  LargeImageIcon,
  LargeAudioIcon,
  Large3DIcon,
  LargeDocumentIcon,
  LargeFlipBookIcon
} from '../../utils/icons/index';
import assetServices from '../../utils/services/asset';
import { useIdPath } from '@/contexts/IdPathProvider';
import categoryServices from '../../utils/services/category';
import s3UploadService from '../../utils/services/s3upload';
import voiceService from '../../utils/services/voice';
import labelServices from '../../utils/services/label';
import FileManager from '../../../../components/FileManager/FileManager';
import AudioFileManager from '../../../../components/FileManager/AudioFileManager';
import type { S3Object } from '../../utils/services/fileManager';
import { Button } from "@/components/UI/button";
import { DynamicLanguageForm } from '@/components/DynamicLanguageForm';
import { useLanguages } from '@/contexts/LanguageContext';
import useAutoTranslate from '@/hooks/useAutoTranslate';
import type { TranslationBudgetEstimate } from '@/hooks/useAutoTranslate';
import { useAIModels } from '@/contexts/AIModelsProvider';
import serviceAPI from '@/utils/services/serviceAPI';
import geminiUsageService from '@/utils/services/geminiUsage';
import { useToast } from '@/templates/Admin/components/Toast';
import { AssetBorrowHistory } from '@/components/admin/projects/assetLoan/AssetBorrowHistory';

// Asset Type from API
interface AssetType {
  id: number;
  name: string;
}

interface VoiceOption {
  code: string;
  name: string;
  level: string;
}

// Category data structure from API
interface CategoryData {
  uuid: string;
  description: string | null;
  parent_uuid: string | null;
  is_show: number;
  index: number | null;
  view_count: number;
  display_mode: number;
  asset_type_id: number | null;      // NEW - null = hiển thị tất cả tabs
  asset_type_name?: string;          // NEW - từ JOIN với sg3d_asset_type
  created_by: number | null;
  created_at: string | null;
  updated_by: number | null;
  updated_at: string | null;
  asset_count?: any;
  category_title: string;
}

// Asset type options for category classification dropdown
const CATEGORY_ASSET_TYPE_OPTIONS = [
  { value: null, label: 'Hiển thị ở tất cả tabs', color: 'gray' },
  { value: 1, label: 'Mô hình 3D', color: 'purple' },
  { value: 2, label: 'Hình ảnh', color: 'blue' },
  { value: 3, label: 'Tài liệu', color: 'orange' },
  { value: 4, label: 'Video', color: 'red' },
  { value: 5, label: 'Âm thanh', color: 'green' },
  { value: 6, label: 'Sách lật', color: 'pink' },
  { value: 7, label: 'Bài viết', color: 'teal' },
];

const AVAILABLE_VOICE_CODES: {[key: string]: {male: VoiceOption[], female: VoiceOption[]}} = {
    'vi': {
      male: [
        { code: 'vi-VN-Standard-D', name: 'Tiêu chuẩn D', level: 'Cơ bản' },
        { code: 'vi-VN-Standard-B', name: 'Tiêu chuẩn B', level: 'Cơ bản' },
        { code: 'vi-VN-Wavenet-D', name: 'WaveNet D', level: 'Nâng cao' }
      ],
      female: [
        { code: 'vi-VN-Standard-A', name: 'Tiêu chuẩn A', level: 'Cơ bản' },
        { code: 'vi-VN-Standard-C', name: 'Tiêu chuẩn C', level: 'Cơ bản' },
        { code: 'vi-VN-Wavenet-A', name: 'WaveNet A', level: 'Nâng cao' }
      ]
    },
    'en': {
      male: [
        { code: 'en-US-Standard-B', name: 'Tiêu chuẩn B', level: 'Cơ bản' },
        { code: 'en-US-Wavenet-D', name: 'WaveNet D', level: 'Nâng cao' },
        { code: 'en-US-Neural2-J', name: 'Neural2 J', level: 'Nâng cao' }
      ],
      female: [
        { code: 'en-US-Standard-A', name: 'Tiêu chuẩn A', level: 'Cơ bản' },
        { code: 'en-US-Wavenet-C', name: 'WaveNet C', level: 'Nâng cao' },
        { code: 'en-US-Neural2-H', name: 'Neural2 H', level: 'Nâng cao' }
      ]
    },
    'zh': {
      male: [
        { code: 'cmn-CN-Standard-C', name: 'Tiêu chuẩn C', level: 'Cơ bản' },
        { code: 'cmn-CN-Wavenet-C', name: 'WaveNet C', level: 'Nâng cao' },
        { code: 'cmn-CN-Neural2-C', name: 'Neural2 C', level: 'Nâng cao' }
      ],
      female: [
        { code: 'cmn-CN-Standard-A', name: 'Tiêu chuẩn A', level: 'Cơ bản' },
        { code: 'cmn-CN-Wavenet-A', name: 'WaveNet A', level: 'Nâng cao' },
        { code: 'cmn-CN-Neural2-A', name: 'Neural2 A', level: 'Nâng cao' }
      ]
    },
    'es': {
      male: [
        { code: 'es-ES-Standard-B', name: 'Tiêu chuẩn B', level: 'Cơ bản' },
        { code: 'es-ES-Wavenet-B', name: 'WaveNet B', level: 'Nâng cao' },
        { code: 'es-ES-Neural2-B', name: 'Neural2 B', level: 'Nâng cao' }
      ],
      female: [
        { code: 'es-ES-Standard-A', name: 'Tiêu chuẩn A', level: 'Cơ bản' },
        { code: 'es-ES-Wavenet-C', name: 'WaveNet C', level: 'Nâng cao' },
        { code: 'es-ES-Neural2-F', name: 'Neural2 F', level: 'Nâng cao' }
      ]
    },
    'de': {
      male: [
        { code: 'de-DE-Standard-B', name: 'Tiêu chuẩn B', level: 'Cơ bản' },
        { code: 'de-DE-Wavenet-B', name: 'WaveNet B', level: 'Nâng cao' },
        { code: 'de-DE-Neural2-B', name: 'Neural2 B', level: 'Nâng cao' }
      ],
      female: [
        { code: 'de-DE-Standard-A', name: 'Tiêu chuẩn A', level: 'Cơ bản' },
        { code: 'de-DE-Wavenet-C', name: 'WaveNet C', level: 'Nâng cao' },
        { code: 'de-DE-Neural2-F', name: 'Neural2 F', level: 'Nâng cao' }
      ]
    }
};

// Asset data structure from API
interface AssetData {
  uuid: string;
  category_uuid: string;
  asset_type_id: number;
  is_show: number;
  index: number | null;
  view_count: number;
  approve: number;
  created_by: number | null;
  created_at: string;
  updated_by: number | null;
  updated_at: string;
  asset_title: string | null;
  asset_type_name: string;
  attributes: {
    [key: string]: {
      id: number;
      description: string | null;
      title: string | null;
      values: string;
    };
  };
}

// Asset variable structure from getFormAssetByType API
interface AssetVariable {
  id: number;
  asset_type_id: number;
  asset_variable_name: string;
  description: string | null;
  index: number | null;
  created_by: number | null;
  created_at: string | null;
  updated_by: number | null;
  updated_at: string | null;
  title: string | null;
}

// Form asset response structure
interface FormAssetResponse {
  assetType: AssetType;
  variables: AssetVariable[];
}

// Combined response data structure
interface CategoryAndAssetResponse {
  asset_type: AssetType;
  parent_category: CategoryData | null;
  requested_level: number;
  parent_uuid: string;
  item_type: 'categories' | 'assets';
  total_items: number;
  categories: CategoryData[];
  assets: AssetData[];
}

interface AssetAssetInfo {
  uuid: string;
  title: string;
  type: string;
  thumbnail?: string;
}

interface AssetManageProps {
  onClose: () => void;
  isSidebarOpen: boolean;
  onAddToCart?: (asset: AssetAssetInfo) => void;
}

export default function AssetManage({ onClose, isSidebarOpen, onAddToCart }: AssetManageProps) {
  const { idPath, namePath } = useIdPath();
  const { languages } = useLanguages();
  const { getActiveProviderName } = useAIModels();
  const { showSuccess, showError, showWarning } = useToast();
  const [translationBudgetEstimate, setTranslationBudgetEstimate] = useState<TranslationBudgetEstimate | null>(null);
  const translationBudgetResolverRef = useRef<((confirmed: boolean) => void) | null>(null);
  const formatEstimateVnd = useCallback((value?: number | null) => {
    return `${Math.round(Number(value || 0)).toLocaleString('vi-VN')} VND`;
  }, []);
  const resolveTranslationBudgetEstimate = useCallback((confirmed: boolean) => {
    translationBudgetResolverRef.current?.(confirmed);
    translationBudgetResolverRef.current = null;
    setTranslationBudgetEstimate(null);
  }, []);
  const confirmBudgetEstimate = useCallback((estimate: TranslationBudgetEstimate) => {
    return new Promise<boolean>((resolve) => {
      translationBudgetResolverRef.current = resolve;
      setTranslationBudgetEstimate(estimate);
    });
  }, []);
  const { isTranslating, translateAllFields, translateSingleField } = useAutoTranslate({
    languages,
    projectId: idPath,
    showSuccess,
    showError,
    showWarning,
    confirmBudgetEstimate
  });
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType | null>(null);
  const [selectedAssetTypeId, setSelectedAssetTypeId] = useState<string>('');
  const [currentData, setCurrentData] = useState<CategoryAndAssetResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [navigationHistory, setNavigationHistory] = useState<{
    level: number;
    parentUuid: string;
    data: CategoryAndAssetResponse;
    parentType?: 'category' | 'asset';
    parentName?: string;
  }[]>([]);

  // State for search
  const [searchTerm, setSearchTerm] = useState('');

  // Edit form state
  const [editFormVisible, setEditFormVisible] = useState(false);
  const [editAsset, setEditAsset] = useState<AssetData | null>(null);
  const [formAssetData, setFormAssetData] = useState<FormAssetResponse | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [thumbnailUploadState, setThumbnailUploadState] = useState<Record<string, { previewUrl: string; uploading: boolean }>>({});

  // Create category form state
  const [createCategoryVisible, setCreateCategoryVisible] = useState(false);
  const [createCategoryLoading, setCreateCategoryLoading] = useState(false);
  const [newCategoryTitles, setNewCategoryTitles] = useState<Record<string, string>>({});
  const [newCategoryDescription, setNewCategoryDescription] = useState('');

  // Edit category name state
  const [editCategoryVisible, setEditCategoryVisible] = useState(false);
  const [editCategory, setEditCategory] = useState<CategoryData | null>(null);
  const [editCategoryData, setEditCategoryData] = useState<any>(null);
  const [editCategoryLoading, setEditCategoryLoading] = useState(false);
  const [editedCategoryTitles, setEditedCategoryTitles] = useState<Record<string, string>>({});
  const [editedCategoryDescription, setEditedCategoryDescription] = useState('');

  // Translate assets in category state
  const [isTranslatingAssets, setIsTranslatingAssets] = useState(false);

  // Create asset form state
  const [createAssetVisible, setCreateAssetVisible] = useState(false);
  const [createAssetLoading, setCreateAssetLoading] = useState(false);
  const [createAssetFormData, setCreateAssetFormData] = useState<FormAssetResponse | null>(null);

  // Voice modal state
  const [createVoiceVisible, setCreateVoiceVisible] = useState(false);
  const [createVoiceLoading, setCreateVoiceLoading] = useState(false);
  const [createVoiceFormData, setCreateVoiceFormData] = useState<FormAssetResponse | null>(null);
  const [createVoiceNarrations, setCreateVoiceNarrations] = useState<Record<string, string>>({});
  const [createVoiceTtsPreviews, setCreateVoiceTtsPreviews] = useState<Record<string, string>>({});
  const [createVoiceTtsGenerating, setCreateVoiceTtsGenerating] = useState(false);
  const [createVoiceSaving, setCreateVoiceSaving] = useState(false);
  const [createVoiceAssetTypeId, setCreateVoiceAssetTypeId] = useState<number | null>(null);

  // File picker states
  const [showAudioFilePicker, setShowAudioFilePicker] = useState(false);
  const [currentAudioInputName, setCurrentAudioInputName] = useState<string>('');
  const [audioFileUploading, setAudioFileUploading] = useState(false);

  // Labels/Tags state
  const [objectLabels, setObjectLabels] = useState<Record<string, Array<{ id: string; name: string; color?: string }>>>({});
  // Track last fetched data to prevent duplicate fetches
  const lastFetchedRef = useRef<{ categories: string[]; assets: string[]; idPath: string } | null>(null);

  // Narrations / TTS state (general create/edit flows)
  const [createNarrations, setCreateNarrations] = useState<Record<string, string>>({});
  const [editNarrations, setEditNarrations] = useState<Record<string, string>>({});
  const [ttsGenerating, setTtsGenerating] = useState(false);
  const [ttsPreviews, setTtsPreviews] = useState<Record<string, string>>({});

  // State to track creating asset type and parent asset UUID
  const [creatingAssetTypeId, setCreatingAssetTypeId] = useState<number | null>(null);
  const [creatingUnderAssetUuid, setCreatingUnderAssetUuid] = useState<string | null>(null);

  // Tab state for asset forms
  const [editAssetActiveTab, setEditAssetActiveTab] = useState<'info' | 'voice' | 'history'>('info');
  const [createAssetActiveTab, setCreateAssetActiveTab] = useState<'info' | 'voice'>('info');

  // Voice-specific state for asset forms
  const [editAssetVoiceNarrations, setEditAssetVoiceNarrations] = useState<Record<string, string>>({});
  const [editAssetVoiceLinks, setEditAssetVoiceLinks] = useState<Record<string, string>>({});
  const [editAssetVoiceGender, setEditAssetVoiceGender] = useState<Record<string, 'male' | 'female'>>({});
  const [editAssetVoiceModel, setEditAssetVoiceModel] = useState<Record<string, string>>({});
  const [editAssetVoiceTtsGenerating, setEditAssetVoiceTtsGenerating] = useState(false);

  // Move functionality state
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveItem, setMoveItem] = useState<{
    type: 'category' | 'asset';
    id: string;
    title: string;
    data: CategoryData | AssetData;
  } | null>(null);
  const [moveTargetCategory, setMoveTargetCategory] = useState<string>('');
  const [moveCategories, setMoveCategories] = useState<CategoryData[]>([]);
  const [moveLoading, setMoveLoading] = useState(false);
  const [moveCategoriesLoading, setMoveCategoriesLoading] = useState(false);
  const [moveCurrentLevel, setMoveCurrentLevel] = useState<number>(0);
  const [moveParentUuid, setMoveParentUuid] = useState<string>('');
  const [moveNavHistory, setMoveNavHistory] = useState<{
    level: number;
    parentUuid: string;
    title: string;
  }[]>([]);

  const [createAssetVoiceNarrations, setCreateAssetVoiceNarrations] = useState<Record<string, string>>({});
  const [createAssetVoiceLinks, setCreateAssetVoiceLinks] = useState<Record<string, string>>({});
  const [createAssetVoiceModel, setCreateAssetVoiceModel] = useState<Record<string, string>>({});
  const [createAssetVoiceTtsGenerating, setCreateAssetVoiceTtsGenerating] = useState(false);

  // Document upload state
  const [documentUploadState, setDocumentUploadState] = useState<Record<string, { previewUrl: string; uploading: boolean; fileName?: string }>>({});

  // Model preview modal state
  const [showModelPreview, setShowModelPreview] = useState(false);
  const [previewModelUrl, setPreviewModelUrl] = useState<string>('');
  const [previewAssetTypeId, setPreviewAssetTypeId] = useState<number>(0);
  const [previewAssetUuid, setPreviewAssetUuid] = useState<string>('');
  const [previewMaterialType, setPreviewMaterialType] = useState<'normal' | 'light1' | 'light2' | 'metal' | 'ceramic' | 'glass' | 'glass_water'>('normal');
  const [previewCurrentSettings, setPreviewCurrentSettings] = useState<any>(null);

  // Form refs for collecting form data
  const formRef = useRef<HTMLFormElement>(null);
  const createCategoryFormRef = useRef<HTMLFormElement>(null);
  const editCategoryFormRef = useRef<HTMLFormElement>(null);
  const createAssetFormRef = useRef<HTMLFormElement>(null);

  // PDF preview modal state
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string>('');
  const [pdfPreviewFileName, setPdfPreviewFileName] = useState<string>('');

  // Debug log


  // Fetch all asset types when component mounts
  useEffect(() => {
    const fetchAssetTypes = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await assetServices.getAllAssetTypes({ id_path: idPath || '' }) as any;

        if (response?.success && Array.isArray(response.data)) {
          setAssetTypes(response.data);
        } else if (response?.data && Array.isArray(response.data)) {
          // Handle case where response doesn't have success field but has data
          setAssetTypes(response.data);
        } else {
          console.error('Invalid response format:', response);
          setError('Không thể tải danh sách loại asset - dữ liệu không hợp lệ');
        }
      } catch (err) {
        console.error('Error fetching asset types:', err);
        setError('Không thể tải danh sách loại asset - lỗi kết nối API');
      } finally {
        setLoading(false);
      }
    };

    if (idPath) {
      fetchAssetTypes();
    } else {
      console.warn('No idPath available, skipping asset types fetch');
      setError('Không có id_path để tải dữ liệu');
    }
  }, [idPath]);

  // Fetch labels when currentData or idPath changes
  useEffect(() => {
    // Skip if no data or idPath
    if (!currentData || !idPath) return;

    const categoryIds = currentData.categories?.map((c: any) => c.uuid) || [];
    const assetIds = (currentData.assets || []).map((a: any) => a.uuid);

    // Skip if no items to fetch
    if (categoryIds.length === 0 && assetIds.length === 0) return;

    // Create a unique key for this fetch request
    const fetchKey = `${idPath}-${categoryIds.join(',')}-${assetIds.join(',')}`;

    // Skip if we've already fetched this exact data
    if (lastFetchedRef.current?.idPath === idPath &&
        lastFetchedRef.current?.categories.join(',') === categoryIds.join(',') &&
        lastFetchedRef.current?.assets.join(',') === assetIds.join(',')) {
      return;
    }

    const fetchLabels = async () => {
      try {
        // Fetch labels for categories
        if (categoryIds.length > 0) {
          const categoryLabelsResponse = await labelServices.getLabelsOfObjects({
            id_path: idPath,
            objectIds: categoryIds,
            objectType: 'category'
          }) as any;

          if (categoryLabelsResponse?.data) {
            const labelsMap: Record<string, Array<{ id: string; name: string; color?: string }>> = {};
            const labelsData = Array.isArray(categoryLabelsResponse.data) ? categoryLabelsResponse.data : [categoryLabelsResponse.data];

            labelsData.forEach((item: any) => {
              if (item.object_id && item.labels) {
                const objectLabels = Array.isArray(item.labels) ? item.labels : [item.labels];
                labelsMap[item.object_id] = objectLabels.map((l: any) => ({
                  id: l.id || l.uuid,
                  name: l.name,
                  color: l.color,
                }));
              }
            });

            setObjectLabels(prev => ({ ...prev, ...labelsMap }));
          }
        }

        // Fetch labels for assets
        if (assetIds.length > 0) {
          const assetLabelsResponse = await labelServices.getLabelsOfObjects({
            id_path: idPath,
            objectIds: assetIds,
            objectType: 'asset'
          }) as any;

          if (assetLabelsResponse?.data) {
            const labelsMap: Record<string, Array<{ id: string; name: string; color?: string }>> = {};
            const labelsData = Array.isArray(assetLabelsResponse.data) ? assetLabelsResponse.data : [assetLabelsResponse.data];

            labelsData.forEach((item: any) => {
              if (item.object_id && item.labels) {
                const objectLabels = Array.isArray(item.labels) ? item.labels : [item.labels];
                labelsMap[item.object_id] = objectLabels.map((l: any) => ({
                  id: l.id || l.uuid,
                  name: l.name,
                  color: l.color,
                }));
              }
            });

            setObjectLabels(prev => {
              console.log('[Labels-Debug] Setting asset labels with keys:', Object.keys(labelsMap), 'data:', labelsMap);
              return { ...prev, ...labelsMap };
            });
          }
        }

        // Mark as fetched
        lastFetchedRef.current = { idPath, categories: categoryIds, assets: assetIds };
      } catch (err) {
        console.error('Error fetching labels:', err);
      }
    };

    fetchLabels();
  }, [currentData, idPath]);

  // Function to fetch categories and assets
  const fetchCategoriesAndAssets = async (
    assetType: number | string,
    level: number = 0,
    parentCategoryUuid: string = '',
    addToHistory: boolean = true,
    parentType: 'category' | 'asset' = 'category',
    parentName?: string
  ) => {
    try {
      setLoading(true);
      setError(null);
      const response = await assetServices.getCategoryAndAssetWithType({
        id_path: idPath || '',
        language_id: 'vn', // Default to Vietnamese
        level,
        parent_category_uuid: parentCategoryUuid,
        asset_type: typeof assetType === 'string' ? parseInt(assetType, 10) : assetType
      }) as any;

      if (response?.success && response.data) {
        const data = response.data as CategoryAndAssetResponse;
        setCurrentData(data);

        // Add to navigation history if requested and include parentType and parentName
        if (addToHistory) {
          setNavigationHistory(prev => [...prev, {
            level,
            parentUuid: parentCategoryUuid,
            data,
            parentType,
            parentName: parentName || (data?.parent_category?.category_title || '')
          }]);
        }
      } else {
        setError('Không thể tải dữ liệu');
      }
    } catch (err) {
      console.error('Error fetching categories and assets:', err);
      setError('Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  // Handle asset type selection
  const handleAssetTypeSelect = (assetType: AssetType) => {
    setSelectedAssetType(assetType);
    setSelectedAssetTypeId(assetType.id.toString());
    setNavigationHistory([]);
    setCurrentData(null);

    // Fetch root level categories/assets for this type
    fetchCategoriesAndAssets(assetType.id, 0, '', false);
  };

  // Handle category double click to drill down
  const handleCategoryDoubleClick = (category: CategoryData) => {
    if (selectedAssetTypeId) {
      const nextLevel = (currentData?.requested_level || 0) + 1;
      fetchCategoriesAndAssets(selectedAssetTypeId, nextLevel, category.uuid, true, 'category', category.category_title);
    }
  };

  // Handle double click on asset (e.g., 3D model) to open it like a folder
  const handleAssetDoubleClick = (asset: AssetData) => {
    // treat model / 3d assets as folders
    const assetTypeString = asset.asset_type_name?.toLowerCase() || '';
    const isModelAsset = assetTypeString.includes('model') || assetTypeString.includes('3d');
    if (!isModelAsset) return;
    if (selectedAssetTypeId) {
      const nextLevel = (currentData?.requested_level || 0) + 1;
      fetchCategoriesAndAssets(selectedAssetTypeId, nextLevel, asset.uuid, true, 'asset', asset.asset_title || '');
    }
  };

  // Handle back navigation
  const handleBackNavigation = () => {
    if (navigationHistory.length > 0) {
      const newHistory = [...navigationHistory];
      newHistory.pop(); // Remove current state

      if (newHistory.length > 0) {
        const previousState = newHistory[newHistory.length - 1];
        setCurrentData(previousState.data);
        setNavigationHistory(newHistory);
      } else {
        // Back to root level
        if (selectedAssetTypeId) {
          fetchCategoriesAndAssets(selectedAssetTypeId, 0, '', false);
          setNavigationHistory([]);
        }
      }
    }
  };

  // Generate breadcrumb path
  const getBreadcrumbPath = () => {
    const path = [selectedAssetType?.name || 'Loại Asset'];

    navigationHistory.forEach((item, index) => {
      if (item.data.parent_category) {
        path.push(item.data.parent_category.category_title);
      }
    });

    return path;
  };

  // Load voice data for an asset from API
  const loadVoiceData = async (asset: AssetData, formData: FormAssetResponse) => {
    try {

      const narrations: Record<string, string> = {};
      const voiceLinks: Record<string, string> = {};
      const genders: Record<string, 'male' | 'female'> = {};
      const models: Record<string, string> = {};

      // Get enabled languages from context
      const enabledLanguages = languages?.filter(lang => lang.enabled) || [];

      // Load voice data for each enabled language
      for (const language of enabledLanguages) {
        try {

          const voiceResponse = await voiceService.getVoiceByTypeIdAndLanguage(
            'asset', // entity_type
            asset.uuid, // entity_id
            language.id, // language_id
            idPath || '' // id_path
          );

          // Handle both wrapped (.data) and direct response formats
          let voiceData = null;
          if (voiceResponse?.data) {
            voiceData = voiceResponse.data;
          } else if (voiceResponse && typeof voiceResponse === 'object' && (voiceResponse.entity_type || voiceResponse.audio_path)) {
            voiceData = voiceResponse;
          }

          if (voiceData) {

            // Populate voice data based on actual API response structure
            // Note: narration_content might not be in voice API, will fallback to asset attributes
            if (voiceData.narration_content) {
              narrations[language.id] = voiceData.narration_content;
            }
            if (voiceData.audio_path) {
              voiceLinks[language.id] = voiceData.audio_path;
            }
            if (voiceData.gender) {
              // Map gender: 'nu' -> 'female', 'nam' -> 'male'
              const mappedGender = voiceData.gender === 'nu' ? 'female' :
                voiceData.gender === 'nam' ? 'male' :
                  voiceData.gender as 'male' | 'female';
              genders[language.id] = mappedGender;
            }
            if (voiceData.voice_model || voiceData.mode) {
              models[language.id] = voiceData.voice_model || voiceData.mode || 'default';
            }

          } else {
            console.log(`⚠️ No voice data found for ${language.id}`);
          }
        } catch (langError) {
          console.warn(`⚠️ Failed to load voice for language ${language.id}:`, langError);
          // Continue with other languages even if one fails
        }
      }

      // Fallback: Try to extract narration content from asset attributes if not found in voice API
      if (asset.attributes && Object.keys(narrations).length === 0) {
        Object.entries(asset.attributes).forEach(([key, attr]) => {
          if (key.includes('narration') || key.includes('thuyetminh')) {
            // Try to match language from the key
            const langMatch = key.match(/_(vn|en|jp|kr)$/) || key.match(/(vn|en|jp|kr)_/);
            const langId = langMatch ? langMatch[1] : 'vn';
            if (!narrations[langId]) {
              narrations[langId] = attr.values || '';
            }
          }
        });
      }

      // Update state with loaded voice data
      setEditAssetVoiceNarrations(narrations);
      setEditAssetVoiceLinks(voiceLinks);
      setEditAssetVoiceGender(genders);
      setEditAssetVoiceModel(models);


    } catch (error) {
      console.error('❌ Error loading voice data:', error);
      // Initialize with empty data if loading fails
      setEditAssetVoiceNarrations({});
      setEditAssetVoiceLinks({});
      setEditAssetVoiceGender({});
      setEditAssetVoiceModel({});
    }
  };

  // Handle preview 3D model
  const handlePreviewModel = (asset: AssetData) => {


    // Find model3d URL from attributes
    const model3dUrl = asset.attributes?.model3d?.values || asset.attributes?.['model3d']?.values;

    if (!model3dUrl) {
      alert('Asset này không có model 3D');
      return;
    }

    // Determine material type from attributes if available
    // Use light_effect to match public page logic
    let materialType: 'normal' | 'light1' | 'light2' | 'metal' | 'ceramic' | 'glass' | 'glass_water' = 'normal';

    if (asset.attributes?.light_effect?.values) {
      const lightEffect = parseInt(asset.attributes.light_effect.values);
      switch (lightEffect) {
        case 0:
          materialType = 'normal';
          break;
        case 1:
          materialType = 'light1';
          break;
        case 2:
          materialType = 'light2';
          break;
        case 3:
          materialType = 'metal';
          break;
        case 4:
          materialType = 'ceramic';
          break;
        case 5:
          materialType = 'glass';
          break;
        case 6:
          materialType = 'glass_water';
          break;
        default:
          materialType = 'normal';
      }
    }


    // Load current settings from asset attributes
    const currentSettings: any = {};

    // Load camera position from individual values
    if (asset.attributes?.x?.values || asset.attributes?.y?.values || asset.attributes?.z?.values) {
      currentSettings.camera = {
        position: {
          x: parseFloat(asset.attributes?.x?.values || '0'),
          y: parseFloat(asset.attributes?.y?.values || '1'),
          z: parseFloat(asset.attributes?.z?.values || '5'),
        },
        up: {
          x: parseFloat(asset.attributes?.up_x?.values || '0'),
          y: parseFloat(asset.attributes?.up_y?.values || '1'),
          z: parseFloat(asset.attributes?.up_z?.values || '0'),
        },
        target: {
          x: parseFloat(asset.attributes?.target_x?.values || '0'),
          y: parseFloat(asset.attributes?.target_y?.values || '0'),
          z: parseFloat(asset.attributes?.target_z?.values || '0'),
        },
      };
    }

    // Load zoom limits from individual values
    if (asset.attributes?.zoom_in?.values || asset.attributes?.zoom_out?.values) {
      currentSettings.zoom = {
        minDistance: parseFloat(asset.attributes?.zoom_in?.values || '0.1'),
        maxDistance: parseFloat(asset.attributes?.zoom_out?.values || '1000'),
      };
    }

    // Get HDR environment
    const hdrEnvValue = asset.attributes?.hdr_environment?.values;
    if (hdrEnvValue) {
      // Check if it's a preset name or a URL
      const presetNames = ['', 'sunset', 'city', 'forest', 'studio', 'apartment', 'dawn', 'night', 'warehouse'];
      if (presetNames.includes(hdrEnvValue)) {
        currentSettings.hdrPreset = hdrEnvValue;
      } else {
        currentSettings.hdrUrl = hdrEnvValue;
      }
    }
    // Get brightness
    if (asset.attributes?.brightness?.values) {
      currentSettings.brightness = parseFloat(asset.attributes.brightness.values);
    }
    if (asset.attributes?.free_rotate?.values) {
      currentSettings.rotationMode = asset.attributes.free_rotate.values;
    }
    // Get auto rotate setting (0/1 or '0'/'1')
    if (asset.attributes?.auto_rotate?.values !== undefined) {
      currentSettings.autoRotate = asset.attributes.auto_rotate.values;
    }
    // Get material type (light_effect) - convert from number to string
    if (asset.attributes?.light_effect?.values !== undefined) {
      const lightEffectNum = asset.attributes.light_effect.values;
      const materialTypeMap: Record<string, string> = {
        '0': 'normal',
        '1': 'light1',
        '2': 'light2',
        '3': 'metal',
        '4': 'ceramic',
        '5': 'glass',
        '6': 'glass_water',
      };
      currentSettings.materialType = materialTypeMap[lightEffectNum] || 'normal';
    }
    if (asset.attributes?.rotation_speed?.values !== undefined) {
      currentSettings.rotationSpeed = asset.attributes.rotation_speed.values;
    }

    setPreviewModelUrl(model3dUrl);
    setPreviewAssetTypeId(asset.asset_type_id);
    setPreviewAssetUuid(asset.uuid);
    setPreviewMaterialType(materialType as any);
    setPreviewCurrentSettings(Object.keys(currentSettings).length > 0 ? currentSettings : null);
    setShowModelPreview(true);
  };

  // Handle edit asset
  const handleEditAsset = async (asset: AssetData) => {
    try {
      setFormLoading(true);
      setEditAsset(asset);


      const response = await assetServices.getFormAssetByType({
        id_path: idPath || '',
        type: asset.asset_type_id.toString()
      }) as any;


      if (response?.success && response.data) {
        setFormAssetData(response.data as FormAssetResponse);

        // Load voice data using API for each enabled language
        await loadVoiceData(asset, response.data as FormAssetResponse);

        setEditFormVisible(true);
      } else if (response?.data) {
        // Handle case where response doesn't have success field but has data
        setFormAssetData(response.data as FormAssetResponse);

        // Load voice data using API for each enabled language
        await loadVoiceData(asset, response.data as FormAssetResponse);

        setEditFormVisible(true);
      } else {
        console.error('Invalid form response:', response);
        setError('Không thể tải biểu mẫu chỉnh sửa hiện vật');
      }
    } catch (err) {
      console.error('Error fetching form asset data:', err);
      setError('Không thể tải biểu mẫu chỉnh sửa hiện vật');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle delete asset
  const handleDeleteAsset = async (asset: AssetData) => {
    if (!confirm(`Bạn có chắc chắn muốn xóa hiện vật "${asset.asset_title || asset.uuid}"?`)) return;
    try {
      setLoading(true);
      const response = await assetServices.deleteAsset({ id_path: idPath || '', asset_uuid: asset.uuid }) as any;
      if (response?.success || response?.message || response === null) {
        alert('Xóa hiện vật thành công!');
        // Refresh current data
        if (selectedAssetTypeId) {
          const currentLevel = currentData?.requested_level || 0;
          const currentParentUuid = navigationHistory.length > 0 ? navigationHistory[navigationHistory.length - 1].parentUuid : '';
          fetchCategoriesAndAssets(selectedAssetTypeId, currentLevel, currentParentUuid, false);
        }
      } else {
        console.error('Delete failed:', response);
        alert('Có lỗi xảy ra khi xóa hiện vật. Vui lòng thử lại.');
      }
    } catch (err) {
      console.error('Error deleting asset:', err);
      alert('Có lỗi xảy ra khi xóa hiện vật. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete category
  const handleDeleteCategory = async (category: CategoryData) => {
    if (!confirm(`Bạn có chắc chắn muốn xóa thư mục "${category.category_title}"?\n\nLưu ý: Tất cả hiện vật và thư mục con bên trong cũng sẽ bị xóa!`)) return;
    try {
      setLoading(true);
      const response = await assetServices.deleteCategory({ id_path: idPath || '', category_uuid: category.uuid }) as any;
      if (response?.success || response?.message || response === null) {
        alert('Xóa thư mục thành công!');
        // Refresh current data
        if (selectedAssetTypeId) {
          const currentLevel = currentData?.requested_level || 0;
          const currentParentUuid = navigationHistory.length > 0 ? navigationHistory[navigationHistory.length - 1].parentUuid : '';
          fetchCategoriesAndAssets(selectedAssetTypeId, currentLevel, currentParentUuid, false);
        }
      } else {
        console.error('Delete category failed:', response);
        alert('Có lỗi xảy ra khi xóa thư mục. Vui lòng thử lại.');
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('Có lỗi xảy ra khi xóa thư mục. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  // Move functionality handlers
  const handleMoveCategory = (category: CategoryData) => {
    setMoveItem({
      type: 'category',
      id: category.uuid,
      title: category.category_title,
      data: category
    });
    setMoveNavHistory([]);
    setMoveCurrentLevel(0);
    setMoveParentUuid('');
    loadMoveCategories(0, '');
    setShowMoveModal(true);
  };

  const handleMoveAsset = (asset: AssetData) => {
    setMoveItem({
      type: 'asset',
      id: asset.uuid,
      title: asset.asset_title || `${asset.asset_type_name} ${asset.uuid.slice(-6)}`,
      data: asset
    });
    setMoveNavHistory([]);
    setMoveCurrentLevel(0);
    setMoveParentUuid('');
    loadMoveCategories(0, '');
    setShowMoveModal(true);
  };

  const loadMoveCategories = async (level: number = 0, parentUuid: string = '') => {
    if (!selectedAssetTypeId || !idPath) return;

    try {
      setMoveCategoriesLoading(true);

      const response = await assetServices.getCategoryAndAssetWithType({
        id_path: idPath,
        language_id: 'vn',
        level,
        parent_category_uuid: parentUuid,
        asset_type: parseInt(selectedAssetTypeId, 10)
      }) as any;

      if (response?.success && response.data) {
        setMoveCategories(response.data.categories || []);
        setMoveCurrentLevel(level);
        setMoveParentUuid(parentUuid);
      } else {
        console.error('Failed to load categories for move:', response);
        setMoveCategories([]);
      }
    } catch (error) {
      console.error('Error loading categories for move:', error);
      showError('Không thể tải danh sách thư mục');
      setMoveCategories([]);
    } finally {
      setMoveCategoriesLoading(false);
    }
  };

  const handleMoveCategoryDoubleClick = (category: CategoryData) => {
    const nextLevel = moveCurrentLevel + 1;

    // The current folder we are in will become a history item
    const currentTitle = moveParentUuid === ''
      ? 'Root'
      : (moveCategories.find(c => c.uuid === moveParentUuid)?.category_title || moveNavHistory[moveNavHistory.length - 1]?.title || 'Thư mục');

    setMoveNavHistory(prev => [...prev, {
      level: moveCurrentLevel,
      parentUuid: moveParentUuid,
      title: currentTitle
    }]);

    loadMoveCategories(nextLevel, category.uuid);
  };

  const handleMoveNavBack = () => {
    if (moveNavHistory.length > 0) {
      const newHistory = [...moveNavHistory];
      const lastState = newHistory.pop();
      if (lastState) {
        setMoveNavHistory(newHistory);
        loadMoveCategories(lastState.level, lastState.parentUuid);
      }
    }
  };

  const executeMove = async () => {
    if (!moveItem || !selectedAssetTypeId || !idPath) return;

    try {
      setMoveLoading(true);

      if (moveItem.type === 'category') {
        // Move category
        const results = await categoryServices.updateCategory({
          id_path: idPath,
          id: moveItem.id,
          body: {
            category_title: moveItem.title,
            description: (moveItem.data as CategoryData).description || '',
            parent_uuid: moveTargetCategory
          }
        }) as any;

        const response = results?.data;
        if (response?.success || response?.message || response === null) {
          showSuccess(`Đã di chuyển thư mục "${moveItem.title}" thành công!`);
        } else {
          throw new Error(response?.error || 'Move category failed');
        }
      } else {
        // Move asset - update the category_uuid field
        const assetData = moveItem.data as AssetData;
        const response = await assetServices.updateAsset({
          id_path: idPath,
          asset_uuid: moveItem.id,
          data: {
            category_uuid: moveTargetCategory,
            // Keep existing asset data
            ...assetData.attributes
          }
        }) as any;

        if (response?.success || response?.message || response === null) {
          showSuccess(`Đã di chuyển asset "${moveItem.title}" thành công!`);
        } else {
          throw new Error(response?.error || 'Move asset failed');
        }
      }

      // Close modal and refresh data
      handleCloseMoveModal();

      // Refresh current view
      if (selectedAssetTypeId) {
        const currentLevel = currentData?.requested_level || 0;
        const currentParentUuid = navigationHistory.length > 0
          ? navigationHistory[navigationHistory.length - 1].parentUuid
          : '';
        fetchCategoriesAndAssets(selectedAssetTypeId, currentLevel, currentParentUuid, false);
      }

    } catch (error) {
      console.error('Error moving item:', error);
      showError(`Không thể di chuyển ${moveItem.type === 'category' ? 'thư mục' : 'asset'}. Vui lòng thử lại.`);
    } finally {
      setMoveLoading(false);
    }
  };

  const handleCloseMoveModal = () => {
    setShowMoveModal(false);
    setMoveItem(null);
    setMoveTargetCategory('');
    setMoveCategories([]);
    setMoveCurrentLevel(0);
    setMoveParentUuid('');
    setMoveNavHistory([]);
  };

  // Close edit form
  const handleCloseEditForm = () => {
    setEditFormVisible(false);
    setEditAsset(null);
    setFormAssetData(null);
    // Reset tab states
    setEditAssetActiveTab('info');
    setEditAssetVoiceNarrations({});
    setEditAssetVoiceLinks({});
    setEditAssetVoiceGender({});
    setEditAssetVoiceModel({});
    setEditAssetVoiceTtsGenerating(false);
  };

  const handleThumbnailFileChange = (variableName: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleThumbnailUpload(variableName, file);
    event.target.value = '';
  };

  const handleThumbnailUpload = async (variableName: string, file: File) => {
    if (!idPath) {
      alert('Không xác định được đường dẫn để tải ảnh lên');
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Vui lòng chọn file ảnh hợp lệ');
      return;
    }

    // Create preview URL for immediate display
    const previewUrl = URL.createObjectURL(file);

    setThumbnailUploadState(prev => ({
      ...prev,
      [variableName]: {
        previewUrl: previewUrl,
        uploading: true,
      },
    }));

    try {
      let processedNamePath = namePath || '';
      if (processedNamePath.endsWith('/admin')) {
        processedNamePath = processedNamePath.slice(0, -6);
      } else if (processedNamePath.endsWith('admin')) {
        processedNamePath = processedNamePath.slice(0, -5);
      } else if (processedNamePath.includes('/admin/')) {
        processedNamePath = processedNamePath.replace('/admin/', '/');
      } else if (processedNamePath.includes('admin/')) {
        processedNamePath = processedNamePath.replace('admin/', '');
      }

      const subfolder = processedNamePath
        ? `${processedNamePath}/upload/images/thumbnails`
        : 'upload/images/thumbnails';

      const uploadResponse = await s3UploadService.uploadToS3(idPath, {
        body: { file },
        subfolder,
      }) as any;

      const uploadedUrl = uploadResponse?.data?.file_url;
      if (!uploadedUrl) {
        throw new Error('Không nhận được đường dẫn sau khi upload');
      }

      // Update the input element with the uploaded URL
      const editFormInput = formRef.current?.elements.namedItem(variableName) as HTMLInputElement | null;
      if (editFormInput) {
        editFormInput.value = uploadedUrl;
      }

      const createFormInput = createAssetFormRef.current?.elements.namedItem(variableName) as HTMLInputElement | null;
      if (createFormInput) {
        createFormInput.value = uploadedUrl;
      }

      setThumbnailUploadState(prev => ({
        ...prev,
        [variableName]: {
          previewUrl: uploadedUrl,
          uploading: false,
        },
      }));
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      alert(error instanceof Error ? error.message : 'Upload thất bại');
      setThumbnailUploadState(prev => ({
        ...prev,
        [variableName]: {
          previewUrl: prev[variableName]?.previewUrl || '',
          uploading: false,
        },
      }));
    }
  };

  // Handle document file change
  const handleDocumentFileChange = (variableName: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleDocumentUpload(variableName, file);
    event.target.value = '';
  };

  // Handle document upload to S3
  const handleDocumentUpload = async (variableName: string, file: File) => {
    if (!idPath) {
      alert('Không xác định được idPath để upload tài liệu');
      return;
    }

    // Validate file type
    const validExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.rtf'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExtension)) {
      alert(`Định dạng file không hỗ trợ. Vui lòng chọn file: ${validExtensions.join(', ')}`);
      return;
    }

    // Set uploading state
    setDocumentUploadState(prev => ({
      ...prev,
      [variableName]: {
        previewUrl: '',
        uploading: true,
        fileName: file.name,
      },
    }));

    try {
      let processedNamePath = namePath || '';
      if (processedNamePath.endsWith('/admin')) {
        processedNamePath = processedNamePath.slice(0, -6);
      } else if (processedNamePath.endsWith('admin')) {
        processedNamePath = processedNamePath.slice(0, -5);
      } else if (processedNamePath.includes('/admin/')) {
        processedNamePath = processedNamePath.replace('/admin/', '/');
      } else if (processedNamePath.includes('admin/')) {
        processedNamePath = processedNamePath.replace('admin/', '');
      }

      const subfolder = processedNamePath
        ? `${processedNamePath}/upload/documents`
        : 'upload/documents';

      const uploadResponse = await s3UploadService.uploadToS3(idPath, {
        body: { file },
        subfolder,
      }) as any;

      const uploadedUrl = uploadResponse?.data?.file_url;
      if (!uploadedUrl) {
        throw new Error('Không nhận được đường dẫn sau khi upload');
      }

      // Update the input element with the uploaded URL
      const editFormInput = formRef.current?.elements.namedItem(variableName) as HTMLInputElement | null;
      if (editFormInput) {
        editFormInput.value = uploadedUrl;
      }

      const createFormInput = createAssetFormRef.current?.elements.namedItem(variableName) as HTMLInputElement | null;
      if (createFormInput) {
        createFormInput.value = uploadedUrl;
      }

      setDocumentUploadState(prev => ({
        ...prev,
        [variableName]: {
          previewUrl: uploadedUrl,
          uploading: false,
          fileName: file.name,
        },
      }));

      showSuccess('Upload tài liệu thành công!', 'Thành công');
    } catch (error) {
      console.error('Error uploading document:', error);
      showError(error instanceof Error ? error.message : 'Upload tài liệu thất bại', 'Lỗi');
      setDocumentUploadState(prev => ({
        ...prev,
        [variableName]: {
          previewUrl: prev[variableName]?.previewUrl || '',
          uploading: false,
          fileName: prev[variableName]?.fileName,
        },
      }));
    }
  };

  // Handle PDF preview - fetch as blob to avoid Content-Disposition download issue
  const handlePdfPreview = async (url: string, fileName: string) => {
    if (!url) return;

    // If it's already a blob URL or data URL, use it directly
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      setPdfPreviewUrl(url);
      setPdfPreviewFileName(fileName);
      setShowPdfPreview(true);
      return;
    }

    // For S3 URLs, fetch as blob to create a blob URL
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch PDF');
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfPreviewUrl(blobUrl);
      setPdfPreviewFileName(fileName);
      setShowPdfPreview(true);
    } catch (err) {
      console.error('Error fetching PDF for preview:', err);
      showError('Không thể xem trước PDF. Vui lòng thử lại.', 'Lỗi');
    }
  };

  // Handle save asset
  const handleSaveAsset = async () => {
    if (!editAsset || !formAssetData) {
      console.log('❌ Missing asset data:', { editAsset: !!editAsset, formAssetData: !!formAssetData });
      return;
    }


    // Check if we're on voice tab and have voice data
    const hasVoiceData = Object.keys(editAssetVoiceNarrations).some(langId =>
      editAssetVoiceNarrations[langId]?.trim()
    );


    // If we're on voice tab, prioritize voice saving
    if (editAssetActiveTab === 'voice') {

      if (hasVoiceData) {

        try {
          setSaveLoading(true);

          // Save only voice data
          const voices: any[] = [];
          const audioPath = 'upload/audio'; // Default path

          Object.entries(editAssetVoiceNarrations).forEach(([languageId, transcript]) => {
            if (transcript && transcript.trim()) {
              const voiceLink = editAssetVoiceLinks[languageId] || '';
              let finalAudioPath = audioPath;

              if (voiceLink) {
                if (voiceLink.includes('s3.hcm-1.cloud.cmctelecom.vn')) {
                  finalAudioPath = voiceLink;
                } else {
                  finalAudioPath = voiceLink.startsWith('/') ? voiceLink.slice(1) : voiceLink;
                }
              }

              voices.push({
                language_id: languageId,
                gender: editAssetVoiceGender[languageId] || "nu",
                audio_path: finalAudioPath,
                transcript: transcript.trim()
              });
            }
          });

          if (voices.length > 0) {
            const voiceApiBody = {
              entity_type: "asset",
              entity_id: editAsset.uuid,
              voices: voices
            };


            const voiceResponse = await voiceService.addNewVoices(idPath || '', { body: voiceApiBody });


            if (voiceResponse?.success || !voiceResponse?.error) {
              alert('Cập nhật voice thành công!');
              // Don't close form, just refresh data
              if (selectedAssetTypeId) {
                const currentLevel = currentData?.requested_level || 0;
                const currentParentUuid = navigationHistory.length > 0
                  ? navigationHistory[navigationHistory.length - 1].parentUuid
                  : '';
                fetchCategoriesAndAssets(selectedAssetTypeId, currentLevel, currentParentUuid, false);
              }
            } else {
              const errorMessage = voiceResponse?.error?.message || voiceResponse?.message || 'Lỗi không xác định khi lưu voice';
              alert(`Có lỗi khi lưu voice:\n${errorMessage}`);
            }
          } else {
            alert('Không có dữ liệu voice để lưu');
          }

          return;
        } catch (err) {
          console.error('Error saving voice data:', err);
          alert('Có lỗi xảy ra khi lưu voice data');
          return;
        } finally {
          setSaveLoading(false);
        }
      } else {
        alert('Vui lòng nhập nội dung thuyết minh trước khi lưu voice');
        return;
      }
    }

    // For asset data saving, we need the form
    if (!formRef.current) {
      alert('Không thể lưu dữ liệu asset. Vui lòng chuyển sang tab "Thông tin Asset" trước khi lưu.');
      return;
    }

    try {
      setSaveLoading(true);

      // Collect form data
      const formData = new FormData(formRef.current);
      const attributes: any = {};

      // Build attributes object from form data
      //   formAssetData.variables.forEach(variable => {
      //     if (variable.asset_variable_name && variable.asset_variable_name !== 'view_count') { // Skip view_count
      //       const value = formData.get(variable.asset_variable_name);
      //       if (value !== null) {
      //         attributes[variable.asset_variable_name] = value.toString();
      //       }
      //     }
      //   });

      formAssetData.variables.forEach(variable => {
        if (variable.asset_variable_name) { // Only include variables with titles (shown in form)

          const value = formData.get(variable.asset_variable_name);
          if (value !== null) {
            attributes[variable.asset_variable_name] = {
              id: variable.id,
              values: value.toString()
            };
          }
        }
      });


      // Extract is_show from showhide attribute
      const is_show = attributes.showhide
        ? parseInt(attributes.showhide.values)
        : undefined;

      // Prepare request body
      const body = {
        asset_type_id: editAsset.asset_type_id,
        is_show,
        attributes
      };

      // Call update API for asset
      const response = await assetServices.updateAsset({
        id_path: idPath || '',
        asset_uuid: editAsset.uuid,
        data: body
      }) as any;

      // Track save results
      let assetSaved = false;
      let voiceSaved = false;
      let voiceError = '';

      // Check if asset was saved successfully
      if (response?.success || response?.message) {
        assetSaved = true;
      }

      // Also save voice data if voice tab has data
      const hasVoiceData = Object.keys(editAssetVoiceNarrations).some(langId =>
        editAssetVoiceNarrations[langId]?.trim()
      );

      if (hasVoiceData) {
        try {
          // Build voices array from voice tab data
          const voices: any[] = [];

          // Get audio path from voice links or use default
          const audioPath = 'upload/audio'; // Default path

          // Create voice entries for each language with narration content
          Object.entries(editAssetVoiceNarrations).forEach(([languageId, transcript]) => {
            if (transcript && transcript.trim()) {
              const voiceLink = editAssetVoiceLinks[languageId] || '';
              let finalAudioPath = audioPath;

              if (voiceLink) {
                // Extract path from S3 URL or use as-is if it's already a path
                if (voiceLink.includes('s3.hcm-1.cloud.cmctelecom.vn')) {
                  finalAudioPath = voiceLink;
                } else {
                  finalAudioPath = voiceLink.startsWith('/') ? voiceLink.slice(1) : voiceLink;
                }
              }

              voices.push({
                language_id: languageId,
                gender: editAssetVoiceGender[languageId] || "nu", // Use selected gender or default to female
                audio_path: finalAudioPath,
                transcript: transcript.trim()
              });
            }
          });

          if (voices.length > 0) {
            // Prepare API body for voice service
            const voiceApiBody = {
              entity_type: "asset",
              entity_id: editAsset.uuid,
              voices: voices
            };


            // Call the voice service API
            const voiceResponse = await voiceService.addNewVoices(idPath || '', { body: voiceApiBody });


            if (voiceResponse?.success || !voiceResponse?.error) {
              voiceSaved = true;
            } else {
              voiceError = voiceResponse?.error?.message || voiceResponse?.message || 'Lỗi không xác định khi lưu voice';
            }
          } else {
            voiceError = 'Không có dữ liệu voice để lưu';
          }
        } catch (err) {
          console.error('Error updating voice data:', err);
          voiceError = err instanceof Error ? err.message : 'Lỗi không xác định khi lưu voice';
        }
      }

      // Show comprehensive feedback
      if (assetSaved && hasVoiceData && voiceSaved) {
        alert('Cập nhật asset và voice thành công!');
        handleCloseEditForm();
      } else if (assetSaved && hasVoiceData && !voiceSaved) {
        alert(`Cập nhật asset thành công, nhưng có lỗi khi lưu voice:\n${voiceError}`);
        // Don't close form so user can retry voice save
      } else if (assetSaved && !hasVoiceData) {
        alert('Cập nhật asset thành công!');
        handleCloseEditForm();
      } else {
        console.error('Asset update failed:', response);
        alert('Có lỗi xảy ra khi cập nhật asset. Vui lòng thử lại.');
        return; // Don't refresh data if asset save failed
      }

      // Refresh current data to show updated asset
      if (selectedAssetTypeId) {
        const currentLevel = currentData?.requested_level || 0;
        const currentParentUuid = navigationHistory.length > 0
          ? navigationHistory[navigationHistory.length - 1].parentUuid
          : '';
        fetchCategoriesAndAssets(selectedAssetTypeId, currentLevel, currentParentUuid, false);
      }
    } catch (err) {
      console.error('Error updating asset:', err);
      alert('Có lỗi xảy ra khi cập nhật asset. Vui lòng thử lại.');
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle create category
  const handleCreateCategory = () => {
    setCreateCategoryVisible(true);
  };

  // Adapter setter for useAutoTranslate to merge results into `newCategoryTitles` state
  const setNewCategoryTitlesFromTranslate = (dataOrUpdater: any) => {
    if (typeof dataOrUpdater === 'function') {
      // Build current titles map from newCategoryTitles
      const currentTitles: Record<string, string> = {};
      languages.forEach((lang) => {
        currentTitles[lang.id] = newCategoryTitles[lang.id] || '';
      });
      const newTitles = dataOrUpdater(currentTitles);
      setNewCategoryTitles(prev => ({
        ...prev,
        ...newTitles
      }));
    } else {
      const newTitles = dataOrUpdater || {};
      setNewCategoryTitles(prev => ({
        ...prev,
        ...newTitles
      }));
    }
  };

  // Handle save category
  const handleSaveCategory = async () => {
    if (!createCategoryFormRef.current || !selectedAssetType) {
      return;
    }

    try {
      setCreateCategoryLoading(true);

      // Collect form data
      const formData = new FormData(createCategoryFormRef.current);
      const description = newCategoryDescription;
      const assetTypeIdValue = formData.get('asset_type_id')?.toString();
      const assetTypeId = assetTypeIdValue === '' ? null : Number(assetTypeIdValue);

      // Collect category titles from newCategoryTitles
      const translations: Record<string, { title: string }> = {};
      let categoryTitleVn = '';

      languages.forEach((lang) => {
        const langCode = lang.id;
        // Use newCategoryTitles if available, otherwise try to get from form
        const titleValue = newCategoryTitles[langCode] || '';

        if (titleValue.trim()) {
          translations[langCode] = { title: titleValue.trim() };
        }

        // Store Vietnamese title for category_title field
        if (langCode === 'vn') {
          categoryTitleVn = titleValue.trim();
        }
      });

      if (!categoryTitleVn) {
        showError('Vui lòng nhập tên category bằng tiếng Việt!', 'Lỗi');
        return;
      }

      // Get current parent UUID
      const currentParentUuid = navigationHistory.length > 0
        ? navigationHistory[navigationHistory.length - 1].parentUuid
        : '';

      // Prepare request body
      const body = {
        category_title: categoryTitleVn,
        translations,
        description: description || null,
        parent_uuid: currentParentUuid || null,
        is_show: 1,
        index: null,
        display_mode: 2,
        asset_type_id: assetTypeId  // NEW - phân loại tab hiển thị
      };


      // Call create API
      const response = await assetServices.createCategory({
        id_path: idPath || '',
        data: body
      }) as any;


      if (response?.success || response?.message || response?.category_uuid) {
        showSuccess('Tạo category thành công!', 'Thành công');
        handleCloseCategoryForm();

        // Refresh current data to show new category
        if (selectedAssetTypeId) {
          const currentLevel = currentData?.requested_level || 0;
          fetchCategoriesAndAssets(selectedAssetTypeId, currentLevel, currentParentUuid, false);
        }
      } else {
        console.error('Create failed:', response);
        showError('Có lỗi xảy ra khi tạo category. Vui lòng thử lại!', 'Lỗi');
      }
    } catch (err) {
      console.error('Error creating category:', err);
      showError('Có lỗi xảy ra khi tạo category. Vui lòng thử lại!', 'Lỗi');
    } finally {
      setCreateCategoryLoading(false);
    }
  };

  // Close create category form
  const handleCloseCategoryForm = () => {
    setCreateCategoryVisible(false);
    setNewCategoryTitles({});
    setNewCategoryDescription('');
    if (createCategoryFormRef.current) {
      createCategoryFormRef.current.reset();
    }
  };

  // Translate all new category titles
  const handleTranslateAllNewCategoryTitles = async () => {
    try {
      // Build current titles map from newCategoryTitles
      const titleMap: Record<string, string> = {};
      languages.forEach((lang) => {
        titleMap[lang.id] = newCategoryTitles[lang.id] || '';
      });

      // Translate titles (no HTML)
      const translated = await translateAllFields(titleMap, setNewCategoryTitlesFromTranslate, 'tên category', false, false);
      if (!translated) return;

      showSuccess('Dịch tất cả tên category thành công!', 'Thành công');
    } catch (error) {
      console.error('Error translating category titles:', error);
    }
  };

  // Adapter setter for useAutoTranslate to merge results into `editedCategoryTitles` state
  const setCategoryTitlesFromTranslate = (dataOrUpdater: any) => {
    if (typeof dataOrUpdater === 'function') {
      // Build current titles map from editedCategoryTitles, falling back to editCategoryData translations
      const currentTitles: Record<string, string> = {};
      languages.forEach((lang) => {
        if (editedCategoryTitles[lang.id]) {
          currentTitles[lang.id] = editedCategoryTitles[lang.id];
        } else {
          const translation = editCategoryData?.translations?.[lang.id];
          currentTitles[lang.id] = translation?.title || '';
        }
      });
      const newTitles = dataOrUpdater(currentTitles);
      setEditedCategoryTitles(prev => ({
        ...prev,
        ...newTitles
      }));
    } else {
      const newTitles = dataOrUpdater || {};
      setEditedCategoryTitles(prev => ({
        ...prev,
        ...newTitles
      }));
    }
  };

  // Handle edit category name
  const handleEditCategoryName = async (category: CategoryData) => {
    setEditCategory(category);
    setEditCategoryLoading(true);

    // Set initial description from category data
    setEditedCategoryDescription(category.description || '');

    try {
      // Fetch category data with translations
      const categoryData = await assetServices.getCategoryById({
        id_path: idPath || '',
        category_uuid: category.uuid
      }) as any;

      if (categoryData?.success || categoryData?.data) {
        const data = categoryData?.data || categoryData;
        // Normalize the data to include category_title from Vietnamese or first available translation
        const normalizedData = {
          ...data,
          // Fallback to display_name or first translation title if category_title doesn't exist
          category_title: data.category_title || data.display_name || data.translations?.vn?.title || (Object.values(data.translations || {}) as any[])[0]?.title || category.category_title
        };
        setEditCategoryData(normalizedData);

        // Update description from detailed data if available
        if (normalizedData.description) {
          setEditedCategoryDescription(normalizedData.description);
        }
      }
      setEditCategoryVisible(true);
    } catch (error) {
      console.error('Error loading category data:', error);
      showError('Không thể tải dữ liệu category!', 'Lỗi');
    } finally {
      setEditCategoryLoading(false);
    }
  };

  // Handle save category name
  const handleSaveCategoryName = async () => {
    if (!editCategory || !editCategoryFormRef.current) {
      return;
    }

    try {
      setEditCategoryLoading(true);

      // Collect form data
      const formData = new FormData(editCategoryFormRef.current);
      const description = editedCategoryDescription;
      const assetTypeIdValue = formData.get('asset_type_id')?.toString();
      const assetTypeId = assetTypeIdValue === '' ? null : Number(assetTypeIdValue);

      // Collect category titles from editedCategoryTitles
      const translations: Record<string, { title: string }> = {};
      let categoryTitleVn = '';

      languages.forEach((lang) => {
        const langCode = lang.id;
        // Use editedCategoryTitles if available, otherwise get from form
        let titleValue = editedCategoryTitles[langCode] ?? '';

        if (!titleValue.trim() && editCategoryData?.translations?.[langCode]) {
          titleValue = editCategoryData.translations[langCode].title || '';
        }

        if (titleValue.trim()) {
          translations[langCode] = { title: titleValue.trim() };
        }

        // Store Vietnamese title for category_title field
        if (langCode === 'vn') {
          categoryTitleVn = titleValue.trim();
        }
      });

      if (!categoryTitleVn) {
        showError('Vui lòng nhập tên category bằng tiếng Việt!', 'Lỗi');
        return;
      }

      // Check if asset_type_id changed and show warning
      const isAssetTypeChanged = assetTypeId !== editCategory.asset_type_id;
      if (isAssetTypeChanged) {
        const oldLabel = CATEGORY_ASSET_TYPE_OPTIONS.find(o => o.value === editCategory.asset_type_id)?.label || 'Tất cả tabs';
        const newLabel = CATEGORY_ASSET_TYPE_OPTIONS.find(o => o.value === assetTypeId)?.label || 'Tất cả tabs';
        const confirmed = window.confirm(
          `⚠️ Bạn đang di chuyển category từ "${oldLabel}" sang "${newLabel}".\n\n` +
          `Lưu ý: Các asset bên trong folder KHÔNG bị ảnh hưởng.\n\n` +
          `Bạn có muốn tiếp tục?`
        );
        if (!confirmed) {
          setEditCategoryLoading(false);
          return;
        }
      }

      // Prepare request body
      const body = {
        category_title: categoryTitleVn,
        translations,
        description: description || editCategory.description,
        parent_uuid: editCategory.parent_uuid,
        is_show: editCategory.is_show,
        index: editCategory.index,
        display_mode: editCategory.display_mode,
        asset_type_id: assetTypeId  // NEW - phân loại tab hiển thị
      };

      // Call update API
      const response = await assetServices.updateCategory({
        id_path: idPath || '',
        category_uuid: editCategory.uuid,
        data: body
      }) as any;

      if (response?.success || response?.message) {
        showSuccess('Cập nhật category thành công!', 'Thành công');
        handleCloseEditCategoryForm();

        // Refresh current data to show updated category
        // If asset_type_id changed, refresh the current tab (category will disappear from this tab)
        if (selectedAssetTypeId) {
          const currentLevel = currentData?.requested_level || 0;
          const currentParentUuid = navigationHistory.length > 0
            ? navigationHistory[navigationHistory.length - 1].parentUuid
            : '';
          fetchCategoriesAndAssets(selectedAssetTypeId, currentLevel, currentParentUuid, false);
        }
      } else {
        console.error('Update failed:', response);
        showError('Có lỗi xảy ra khi cập nhật category. Vui lòng thử lại!', 'Lỗi');
      }
    } catch (err) {
      console.error('Error updating category:', err);
      showError('Có lỗi xảy ra khi cập nhật category. Vui lòng thử lại!', 'Lỗi');
    } finally {
      setEditCategoryLoading(false);
    }
  };

  // Close edit category form
  const handleCloseEditCategoryForm = () => {
    setEditCategoryVisible(false);
    setEditCategory(null);
    setEditCategoryData(null);
    setEditedCategoryTitles({});
    setEditedCategoryDescription('');
    if (editCategoryFormRef.current) {
      editCategoryFormRef.current.reset();
    }
  };

  // Translate all category titles
  const handleTranslateAllCategoryTitles = async () => {
    try {
      // Build current titles map from editedCategoryTitles and editCategoryData
      const titleMap: Record<string, string> = {};
      languages.forEach((lang) => {
        if (editedCategoryTitles[lang.id]) {
          titleMap[lang.id] = editedCategoryTitles[lang.id];
        } else {
          const translation = editCategoryData?.translations?.[lang.id];
          titleMap[lang.id] = translation?.title || '';
        }
      });

      // Translate titles (no HTML)
      const translated = await translateAllFields(titleMap, setCategoryTitlesFromTranslate, 'tên category', false, false);
      if (!translated) return;

      showSuccess('Dịch tất cả tên category thành công!', 'Thành công');
    } catch (error) {
      console.error('Error translating category titles:', error);
    }
  };

  const buildTranslateAssetsPayload = useCallback((category: CategoryData) => ({
    asset_type_id: selectedAssetType?.id || 0,
    category_uuid: category.uuid,
    languages: languages
      .filter(lang => lang.enabled !== false)
      .map(lang => lang.id),
    source_language: 'vn',
    fields: ['title', 'mieu_ta']
  }), [languages, selectedAssetType?.id]);

  const handleOpenTranslateAssetsEstimate = async (category: CategoryData) => {
    if (!selectedAssetType) {
      return;
    }
    if (!idPath) {
      showError('Không xác định được dự án để kiểm tra ngân sách dịch.', 'Lỗi');
      return;
    }

    setEditCategory(category);
    try {
      const estimateResponse = await assetServices.estimateTranslateAssetsByCategory({
        id_path: idPath || '',
        data: buildTranslateAssetsPayload(category)
      });

      if (!estimateResponse?.success) {
        showError(estimateResponse?.message || 'Không thể ước lượng chi phí dịch tài nguyên', 'Lỗi');
        return;
      }

      const billableCharacterCount = Number(estimateResponse.billable_character_count || 0);
      const targetCount = Number(estimateResponse.target_languages?.length || 0);
      const estimate = await geminiUsageService.getTranslationEstimate({
        projectId: idPath,
        label: `thông tin ${category.asset_count || 0} tài nguyên`,
        characterCount: billableCharacterCount,
        targetCount: 1
      });
      const confirmed = await confirmBudgetEstimate({
        ...estimate,
        targetCount,
        billableCharacterCount,
      });

      if (confirmed) {
        await handleTranslateAllAssetsInCategory(category);
      }
    } catch (error: any) {
      console.error('Error estimating asset translation:', error);
      showError(error?.message || 'Không thể ước lượng chi phí dịch tài nguyên', 'Lỗi');
    }
  };

  // Handle translate all assets in category
  const handleTranslateAllAssetsInCategory = async (category: CategoryData = editCategory as CategoryData) => {
    if (!category || !selectedAssetType) {
      return;
    }

    try {
      setIsTranslatingAssets(true);

      // Get the active model from AIModelsProvider
      const activeProviderName = getActiveProviderName('translate') || 'google';

      const response = await assetServices.translateAssetsByCategory({
        id_path: idPath || '',
        data: {
          ...buildTranslateAssetsPayload(category),
          model_translate: activeProviderName,
        }
      });

      if (response?.success) {
        showSuccess(
          `Dịch thành công! Đã dịch ${(response?.statistics?.translations_created || 0) + (response?.statistics?.translations_updated || 0)} trường`,
          'Thành công'
        );

        // Refresh the current data to show updated translations
        if (selectedAssetTypeId) {
          const currentLevel = currentData?.requested_level || 0;
          const currentParentUuid = navigationHistory.length > 0
            ? navigationHistory[navigationHistory.length - 1].parentUuid
            : '';
          fetchCategoriesAndAssets(selectedAssetTypeId, currentLevel, currentParentUuid, false);
        }
      } else {
        showError(response?.message || 'Có lỗi xảy ra khi dịch assets', 'Lỗi');
      }
    } catch (error: any) {
      console.error('Error translating assets:', error);
      showError(error.message || 'Có lỗi xảy ra khi dịch assets', 'Lỗi');
    } finally {
      setIsTranslatingAssets(false);
    }
  };

  // Handle create asset
  const handleCreateAsset = async (parentAssetUuid: string | null = null) => {
    if (!selectedAssetType) {
      return;
    }

    try {
      setCreateAssetLoading(true);

      // default selected create type to selectedAssetType unless we are explicitly creating under a model and we want to allow choosing another type
      const typeToFetch = creatingAssetTypeId ?? selectedAssetType.id;
      setCreatingAssetTypeId(typeToFetch);

      const response = await assetServices.getFormAssetByType({
        id_path: idPath || '',
        type: typeToFetch.toString()
      }) as any;

      if (response?.success && response.data) {
        setCreateAssetFormData(response.data as FormAssetResponse);
        setCreateAssetVisible(true);
        setCreateNarrations({});
        setTtsPreviews({});
        setCreatingUnderAssetUuid(parentAssetUuid);
      } else if (response?.data) {
        setCreateAssetFormData(response.data as FormAssetResponse);
        setCreateAssetVisible(true);
        setCreateNarrations({});
        setTtsPreviews({});
        setCreatingUnderAssetUuid(parentAssetUuid);
      } else {
        console.error('Invalid form response:', response);
        alert('Không thể tải form tạo asset');
      }
    } catch (err) {
      console.error('Error fetching form asset data:', err);
      alert('Không thể tải form tạo asset');
    } finally {
      setCreateAssetLoading(false);
    }
  };

  // Handle save asset
  const handleSaveNewAsset = async () => {
    if (!selectedAssetType || !createAssetFormData) {

      return;
    }


    // Check if we have voice data
    const hasVoiceData = Object.keys(createAssetVoiceNarrations).some(langId =>
      createAssetVoiceNarrations[langId]?.trim()
    );

    // For create asset, we always need the form for basic asset creation
    if (!createAssetFormRef.current) {
      alert('Không thể tạo asset. Vui lòng đảm bảo form được tải đầy đủ.');
      return;
    }

    try {
      setCreateAssetLoading(true);

      // Collect form data
      const formData = new FormData(createAssetFormRef.current);
      const attributes: any = {};



      // Build attributes from form; also add parent_display_id if we are creating under asset
      createAssetFormData.variables.forEach(variable => {
        // Include all variables that have a name
        let value = formData.get(variable.asset_variable_name);

        // Also check upload states for controlled inputs
        if (!value || value === '') {
          // Check thumbnail state
          if (variable.asset_variable_name.includes('thumbnail') && thumbnailUploadState[variable.asset_variable_name]?.previewUrl) {
            value = thumbnailUploadState[variable.asset_variable_name].previewUrl;
          }
          // Check document state
          if ((variable.asset_variable_name.includes('document') || variable.asset_variable_name.includes('tai_lieu') || variable.asset_variable_name.includes('file')) && documentUploadState[variable.asset_variable_name]?.previewUrl) {
            value = documentUploadState[variable.asset_variable_name].previewUrl;
          }
        }

        if (value !== null && value !== '') {
          attributes[variable.asset_variable_name] = {
            id: variable.id,
            values: value.toString()
          };
        }
      });




      // if creating under an asset, set parent_display_id attribute
      const parentAssetUuid = creatingUnderAssetUuid ?? (navigationHistory.length > 0 && navigationHistory[navigationHistory.length - 1]?.parentType === 'asset' ? navigationHistory[navigationHistory.length - 1].parentUuid : '');
      if (parentAssetUuid) {
        const parentVar = createAssetFormData.variables.find(v => v.asset_variable_name === 'parent_display_id');
        if (parentVar) {
          attributes['parent_display_id'] = {
            id: parentVar.id,
            values: parentAssetUuid,
          };
        } else {
          attributes['parent_display_id'] = {
            id: 0,
            values: parentAssetUuid,
          };
        }
      }

      // Get current category (if parent's type is category)
      let currentCategoryUuid = '';

      // Check if we're inside a category (not at root level)
      if (navigationHistory.length > 0) {
        const lastNav = navigationHistory[navigationHistory.length - 1];
        if (lastNav.parentType === 'category') {
          currentCategoryUuid = lastNav.parentUuid;
        } else if (lastNav.parentType === 'asset') {
          // If we're creating under an asset, find the category from earlier in history
          for (let i = navigationHistory.length - 2; i >= 0; i--) {
            if (navigationHistory[i].parentType === 'category') {
              currentCategoryUuid = navigationHistory[i].parentUuid;
              break;
            }
          }
        }
      }

      // Also check currentData for parent category
      if (!currentCategoryUuid && currentData?.parent_category?.uuid) {
        currentCategoryUuid = currentData.parent_category.uuid;
      }

      // Validate required fields
      if (!currentCategoryUuid) {
        alert('Vui lòng vào một danh mục trước khi tạo asset');
        setCreateAssetLoading(false);
        return;
      }

      const typeToUse = creatingAssetTypeId ?? selectedAssetType.id;

      // Prepare
      const body = {
        asset_type_id: typeToUse,
        category_uuid: currentCategoryUuid,
        attributes: attributes
      };

      // Call create API
      const response = await assetServices.createNewAsset(idPath || '', {
        body: body
      });

      let createdAssetUuid = '';
      if (response?.asset_uuid) {
        createdAssetUuid = response.asset_uuid;
      } else if (response?.data?.asset_uuid) {
        createdAssetUuid = response.data.asset_uuid;
      } else if (response?.data?.uuid) {
        // API returns uuid in data object
        createdAssetUuid = response.data.uuid;
      }

      // Track save results
      let assetSaved = false;
      let voiceSaved = false;
      let voiceError = '';

      // Check if asset was created successfully
      // Check for success: true OR valid uuid in response
      if (response?.success === true || createdAssetUuid) {
        assetSaved = true;
      } else if (response?.success === false || response?.error) {
        // Explicitly failed
        assetSaved = false;
        console.error('Failed to create asset:', response?.message || response?.error);
      }

      // Also save voice data if voice tab has data and we have a created asset UUID
      const hasVoiceData = Object.keys(createAssetVoiceNarrations).some(langId =>
        createAssetVoiceNarrations[langId]?.trim()
      );

      if (hasVoiceData && createdAssetUuid) {
        try {
          // Build voices array from voice tab data
          const voices: any[] = [];

          // Get audio path from voice links or use default
          const audioPath = 'upload/audio'; // Default path

          // Create voice entries for each language with narration content
          Object.entries(createAssetVoiceNarrations).forEach(([languageId, transcript]) => {
            if (transcript && transcript.trim()) {
              const voiceLink = createAssetVoiceLinks[languageId] || '';
              let finalAudioPath = audioPath;

              if (voiceLink) {
                // Extract path from S3 URL or use as-is if it's already a path
                if (voiceLink.includes('s3.hcm-1.cloud.cmctelecom.vn')) {
                  finalAudioPath = voiceLink;
                } else {
                  finalAudioPath = voiceLink.startsWith('/') ? voiceLink.slice(1) : voiceLink;
                }
              }

              voices.push({
                language_id: languageId,
                gender: "nu",
                audio_path: finalAudioPath,
                transcript: transcript.trim()
              });
            }
          });

          if (voices.length > 0) {
            // Prepare API body for voice service
            const voiceApiBody = {
              entity_type: "asset",
              entity_id: createdAssetUuid,
              voices: voices
            };


            // Call the voice service API
            const voiceResponse = await voiceService.addNewVoices(idPath || '', { body: voiceApiBody });


            if (voiceResponse?.success || !voiceResponse?.error) {
              voiceSaved = true;
            } else {
              voiceError = voiceResponse?.error?.message || voiceResponse?.message || 'Lỗi không xác định khi tạo voice';
            }
          } else {
            voiceError = 'Không có dữ liệu voice để tạo';
          }
        } catch (err) {
          console.error('Error creating voice data:', err);
          voiceError = err instanceof Error ? err.message : 'Lỗi không xác định khi tạo voice';
        }
      } else if (hasVoiceData && !createdAssetUuid) {
        voiceError = 'Không có UUID của asset để tạo voice';
      }

      // Show comprehensive feedback
      if (assetSaved && hasVoiceData && voiceSaved) {
        alert('Tạo asset và voice thành công!');
        handleCloseAssetForm();
      } else if (assetSaved && hasVoiceData && !voiceSaved) {
        alert(`Tạo asset thành công, nhưng có lỗi khi tạo voice:\n${voiceError}\nAsset UUID: ${createdAssetUuid}`);
        // Don't close form so user can retry voice creation
      } else if (assetSaved && !hasVoiceData) {
        alert('Tạo asset thành công!');
        handleCloseAssetForm();
      } else {
        console.error('Asset creation failed:', response);
        alert('Có lỗi xảy ra khi tạo asset. Vui lòng thử lại.');
        return; // Don't refresh data if asset creation failed
      }

      // Refresh current data to show new asset
      if (selectedAssetTypeId) {
        const currentLevel = currentData?.requested_level || 0;
        const currentParentUuid = navigationHistory.length > 0
          ? navigationHistory[navigationHistory.length - 1].parentUuid
          : '';
        fetchCategoriesAndAssets(selectedAssetTypeId, currentLevel, currentParentUuid, false);
      }
    } catch (err) {
      console.error('Error creating asset:', err);
      alert('Có lỗi xảy ra khi tạo asset. Vui lòng thử lại.');
    } finally {
      setCreateAssetLoading(false);
    }
  };

  // Handle create voice modal opening
  const handleCreateVoice = async (parentAssetUuid: string | null = null) => {
    try {
      setCreateVoiceLoading(true);

      // Find audio asset type (fallback to selectedAssetType)
      const audioType = assetTypes.find(t => t.name?.toLowerCase().includes('audio'));
      const typeToFetch = createVoiceAssetTypeId ?? audioType?.id ?? selectedAssetType?.id;
      if (!typeToFetch) {
        alert('Không tìm thấy loại Voice (audio) để tạo');
        return;
      }
      setCreateVoiceAssetTypeId(typeToFetch);

      const response = await assetServices.getFormAssetByType({ id_path: idPath || '', type: String(typeToFetch) }) as any;

      if (response?.success && response.data) {
        setCreateVoiceFormData(response.data as FormAssetResponse);
        setCreateVoiceVisible(true);
        setCreateVoiceNarrations({});
        setCreateVoiceTtsPreviews({});
        setCreatingUnderAssetUuid(parentAssetUuid);
      } else if (response?.data) {
        setCreateVoiceFormData(response.data as FormAssetResponse);
        setCreateVoiceVisible(true);
        setCreateVoiceNarrations({});
        setCreateVoiceTtsPreviews({});
        setCreatingUnderAssetUuid(parentAssetUuid);
      } else {
        console.error('Invalid form response:', response);
        alert('Không thể tải form tạo voice');
      }
    } catch (err) {
      console.error('Error fetching form asset data for voice:', err);
      alert('Không thể tải form tạo voice');
    } finally {
      setCreateVoiceLoading(false);
    }
  };

  const handleCloseVoiceForm = () => {
    setCreateVoiceVisible(false);
    setCreateVoiceFormData(null);
    setCreateVoiceNarrations({});
    setCreateVoiceTtsPreviews({});
    setCreateVoiceTtsGenerating(false);
    setCreateVoiceAssetTypeId(null);
    if (createAssetFormRef.current) createAssetFormRef.current.reset();
  };

  // Convert narration map into API attributes matching variable definitions
  const convertNarrationsToAttributes = (variables: AssetVariable[] | null | undefined, narrations: Record<string, string>) => {
    const attrs: any = {};
    if (!variables || !narrations) return attrs;
    Object.entries(narrations).forEach(([langId, text]) => {
      if (!text || !text.trim()) return;
      const language = languages.find(l => l.id === langId);
      const code = (language?.code || langId).toLowerCase();
      // First try to find variable ending with _lang or containing _lang_
      const specificVar = variables.find(v => v.asset_variable_name.toLowerCase().endsWith(`_${langId.toLowerCase()}`) || v.asset_variable_name.toLowerCase().endsWith(`_${code}`) || v.asset_variable_name.toLowerCase().includes(`_${langId.toLowerCase()}_`) || v.asset_variable_name.toLowerCase().includes(`_${code}_`));
      if (specificVar) {
        attrs[specificVar.asset_variable_name] = { id: specificVar.id, values: text };
        return;
      }
      // fallback to any narration-like variable
      const genericVar = variables.find(v => ['narration', 'voice', 'audio', 'mieu_ta', 'description'].some(k => v.asset_variable_name.toLowerCase().includes(k)));
      if (genericVar) {
        attrs[genericVar.asset_variable_name] = { id: genericVar.id, values: text };
      }
    });
    return attrs;
  };

  // Helper to get correct voice code
  const getVoiceForLanguage = (langCode: string, gender: string = 'female', voiceModel: string = 'default') => {
    if (voiceModel && voiceModel !== 'default' && voiceModel !== '') return voiceModel;

    const normalizedCode = langCode?.toLowerCase();
    const normalizedGender = gender === 'nam' || gender === 'male' ? 'male' : 'female';

    let code = normalizedCode;
    // Standardize code to match AVAILABLE_VOICE_CODES keys
    if (code === 'vn' || code === 'vi-vn') code = 'vi';
    else if (code.startsWith('en')) code = 'en';
    else if (code.startsWith('zh') || code.startsWith('cmn')) code = 'zh';
    else if (code.startsWith('es')) code = 'es';
    else if (code.startsWith('de')) code = 'de';

    let langVoices = AVAILABLE_VOICE_CODES[code];
    if (!langVoices) {
      langVoices = AVAILABLE_VOICE_CODES['vi'];
    }

    const voices = langVoices[normalizedGender as 'male' | 'female'];
    return voices?.[0]?.code || 'vi-VN-Standard-A';
  };

  // Generate TTS for voice modal
  const generateCreateVoiceTts = async ({ targetLangId }: { targetLangId?: string } = {}) => {
    const source = createVoiceNarrations;
    if (!source || Object.keys(source).length === 0) {
      alert('Vui lòng nhập nội dung thuyết minh trước khi tạo TTS');
      return;
    }

    const provider = getActiveProviderName('tts');
    const langList = languages && languages.length ? languages : [{ id: 'vn', name: 'Vietnamese', code: 'VN' }];
    const targets = targetLangId ? langList.filter(l => l.id === targetLangId) : langList.filter(l => l.enabled !== false);
    if (!targets.length) {
      alert('Không có ngôn ngữ để tạo TTS');
      return;
    }

    setCreateVoiceTtsGenerating(true);
    try {
      for (const lang of targets) {
        const text = source[lang.id] || source[(lang.code || '').toLowerCase()] || Object.values(source).find(Boolean) || '';
        if (!text || !text.trim()) { alert(`Chưa có nội dung cho ${lang.name}`); continue; }
        const voiceModel = getVoiceForLanguage(lang.code, 'female', 'default');
        const res = await serviceAPI.textToSpeech({ id_path: idPath || '', supplier: provider, text, voice: voiceModel, format: 'mp3', uploadToS3: true });
        const fileUrl = res?.data?.file?.url || res?.data?.file_url || res?.uploadInfo?.s3Url || res?.file_url || res?.url || (res?.data && (res.data.fileUrl || res.data.file_url));
        if (fileUrl) {
          setCreateVoiceTtsPreviews(prev => ({ ...prev, [lang.id]: fileUrl }));
          // Try to fill audio field in main info tab if it exists
          if (createVoiceFormData?.variables) {
            const audioName = createVoiceFormData.variables.find(v => v.asset_variable_name.toLowerCase().includes('audio') || v.asset_variable_name.toLowerCase().includes('voice'))?.asset_variable_name;
            if (audioName) {
              const inputEl = createAssetFormRef.current?.elements.namedItem(audioName) as HTMLInputElement | null;
              if (inputEl) {
                inputEl.value = fileUrl;
                // Update preview for the audio field
                setCreateVoiceTtsPreviews(prev => ({ ...prev, [audioName]: fileUrl }));
                // Trigger input event
                const event = new Event('input', { bubbles: true });
                inputEl.dispatchEvent(event);
              }
            }
          }
        }
      }
      showSuccess('Tạo TTS thành công');
    } catch (err) {
      console.error('TTS generation error (create voice)', err);
      showError('Không thể tạo giọng nói từ TTS', 'Lỗi');
    } finally {
      setCreateVoiceTtsGenerating(false);
    }
  };

  // Generate TTS for create asset form
  const generateTtsForLanguages = async ({ targetLangId }: { targetLangId?: string } = {}) => {
    const source = createNarrations;
    if (!source || Object.keys(source).length === 0) {
      alert('Vui lòng nhập nội dung thuyết minh trước khi tạo TTS');
      return;
    }

    const provider = getActiveProviderName('tts');
    const langList = languages && languages.length ? languages : [{ id: 'vn', name: 'Vietnamese', code: 'VN' }];
    const targets = targetLangId ? langList.filter(l => l.id === targetLangId) : langList.filter(l => l.enabled !== false);
    if (!targets.length) {
      alert('Không có ngôn ngữ để tạo TTS');
      return;
    }

    setTtsGenerating(true);
    try {
      for (const lang of targets) {
        const text = source[lang.id] || source[(lang.code || '').toLowerCase()] || Object.values(source).find(Boolean) || '';
        if (!text || !text.trim()) {
          showWarning(`Chưa có nội dung cho ${lang.name}`);
          continue;
        }

        const voiceModel = getVoiceForLanguage(lang.code, 'female', 'default');
        const res = await serviceAPI.textToSpeech({
          id_path: idPath || '',
          supplier: provider,
          text,
          voice: voiceModel,
          format: 'mp3',
          uploadToS3: true
        });

        const fileUrl = res?.data?.file?.url || res?.data?.file_url || res?.uploadInfo?.s3Url || res?.file_url || res?.url || (res?.data && (res.data.fileUrl || res.data.file_url));
        if (fileUrl) {
          setTtsPreviews(prev => ({ ...prev, [lang.id]: fileUrl }));

          // Try to fill audio field if exists
          if (createAssetFormData?.variables) {
            const audioVariable = createAssetFormData.variables.find(v =>
              v.asset_variable_name.toLowerCase().includes('audio') ||
              v.asset_variable_name.toLowerCase().includes('voice')
            );

            if (audioVariable) {
              const inputEl = createAssetFormRef.current?.elements.namedItem(audioVariable.asset_variable_name) as HTMLInputElement | null;
              if (inputEl) {
                inputEl.value = fileUrl;
                // Update preview for the audio field so it shows in the form
                setTtsPreviews(prev => ({ ...prev, [audioVariable.asset_variable_name]: fileUrl }));
                // Also trigger the onChange event to ensure preview shows
                const event = new Event('input', { bubbles: true });
                inputEl.dispatchEvent(event);
              }
            }
          }
        }
      }
      showSuccess('Tạo TTS thành công');
    } catch (err) {
      console.error('TTS generation error (create asset)', err);
      showError('Không thể tạo giọng nói từ TTS', 'Lỗi');
    } finally {
      setTtsGenerating(false);
    }
  };

  // Generate TTS for edit asset form
  const generateEditTts = async ({ targetLangId }: { targetLangId?: string } = {}) => {
    const source = editNarrations;
    if (!source || Object.keys(source).length === 0) {
      alert('Vui lòng nhập nội dung thuyết minh trước khi tạo TTS');
      return;
    }

    const provider = getActiveProviderName('tts');
    const langList = languages && languages.length ? languages : [{ id: 'vn', name: 'Vietnamese', code: 'VN' }];
    const targets = targetLangId ? langList.filter(l => l.id === targetLangId) : langList.filter(l => l.enabled !== false);
    if (!targets.length) {
      alert('Không có ngôn ngữ để tạo TTS');
      return;
    }

    setTtsGenerating(true);
    try {
      for (const lang of targets) {
        const text = source[lang.id] || source[(lang.code || '').toLowerCase()] || Object.values(source).find(Boolean) || '';
        if (!text || !text.trim()) {
          showWarning(`Chưa có nội dung cho ${lang.name}`);
          continue;
        }

        const voiceModel = getVoiceForLanguage(lang.code, 'female', 'default');
        const res = await serviceAPI.textToSpeech({
          id_path: idPath || '',
          supplier: provider,
          text,
          voice: voiceModel,
          format: 'mp3',
          uploadToS3: true
        });

        const fileUrl = res?.data?.file?.url || res?.data?.file_url || res?.uploadInfo?.s3Url || res?.file_url || res?.url || (res?.data && (res.data.fileUrl || res.data.file_url));
        if (fileUrl) {
          setTtsPreviews(prev => ({ ...prev, [lang.id]: fileUrl }));

          // Try to fill audio field if exists
          if (formAssetData?.variables) {
            const audioVariable = formAssetData.variables.find(v =>
              v.asset_variable_name.toLowerCase().includes('audio') ||
              v.asset_variable_name.toLowerCase().includes('voice')
            );

            if (audioVariable) {
              const inputEl = formRef.current?.elements.namedItem(audioVariable.asset_variable_name) as HTMLInputElement | null;
              if (inputEl) {
                inputEl.value = fileUrl;
                // Update preview for the audio field so it shows in the form
                setTtsPreviews(prev => ({ ...prev, [audioVariable.asset_variable_name]: fileUrl }));
                // Also trigger the onChange event to ensure preview shows
                const event = new Event('input', { bubbles: true });
                inputEl.dispatchEvent(event);
              }
            }
          }
        }
      }
      showSuccess('Tạo TTS thành công');
    } catch (err) {
      console.error('TTS generation error (edit asset)', err);
      showError('Không thể tạo giọng nói từ TTS', 'Lỗi');
    } finally {
      setTtsGenerating(false);
    }
  };

  // Generate TTS for edit asset voice tab
  const generateEditAssetVoiceTts = async ({ targetLangId }: { targetLangId?: string } = {}) => {
    const source = editAssetVoiceNarrations;
    if (!source || Object.keys(source).length === 0) {
      alert('Vui lòng nhập nội dung thuyết minh trước khi tạo TTS');
      return;
    }

    const provider = getActiveProviderName('tts');
    const langList = languages && languages.length ? languages : [{ id: 'vn', name: 'Vietnamese', code: 'VN' }];
    const targets = targetLangId ? langList.filter(l => l.id === targetLangId) : langList.filter(l => l.enabled !== false);
    if (!targets.length) {
      alert('Không có ngôn ngữ để tạo TTS');
      return;
    }

    setEditAssetVoiceTtsGenerating(true);
    try {
      for (const lang of targets) {
        const text = source[lang.id] || source[(lang.code || '').toLowerCase()] || Object.values(source).find(Boolean) || '';
        if (!text || !text.trim()) {
          showWarning(`Chưa có nội dung cho ${lang.name}`);
          continue;
        }

        const gender = editAssetVoiceGender[lang.id] || 'female';
        const voiceModel = editAssetVoiceModel[lang.id] || 'default';
        const mappedVoice = getVoiceForLanguage(lang.code, gender, voiceModel);

        const res = await serviceAPI.textToSpeech({
          id_path: idPath || '',
          supplier: provider,
          text,
          voice: mappedVoice,
          format: 'mp3',
          uploadToS3: true
        });

        const fileUrl = res?.data?.file?.url || res?.data?.file_url || res?.uploadInfo?.s3Url || res?.file_url || res?.url || (res?.data && (res.data.fileUrl || res.data.file_url));
        if (fileUrl) {
          setEditAssetVoiceLinks(prev => ({ ...prev, [lang.id]: fileUrl }));

          // Try to fill audio field in main info tab if it exists
          if (formAssetData?.variables) {
            const audioVariable = formAssetData.variables.find(v =>
              v.asset_variable_name.toLowerCase().includes('audio') ||
              v.asset_variable_name.toLowerCase().includes('voice')
            );

            if (audioVariable) {
              const inputEl = formRef.current?.elements.namedItem(audioVariable.asset_variable_name) as HTMLInputElement | null;
              if (inputEl) {
                inputEl.value = fileUrl;
                // Update preview for the audio field so it shows in the form
                setTtsPreviews(prev => ({ ...prev, [audioVariable.asset_variable_name]: fileUrl }));
                // Also trigger the input event to ensure any listeners are notified
                const event = new Event('input', { bubbles: true });
                inputEl.dispatchEvent(event);
              }
            }
          }
        }
      }
      showSuccess('Tạo TTS thành công');
    } catch (err) {
      console.error('TTS generation error (edit asset voice)', err);
      showError('Không thể tạo giọng nói từ TTS', 'Lỗi');
    } finally {
      setEditAssetVoiceTtsGenerating(false);
    }
  };

  // Generate TTS for create asset voice tab
  const generateCreateAssetVoiceTts = async ({ targetLangId }: { targetLangId?: string } = {}) => {
    const source = createAssetVoiceNarrations;
    if (!source || Object.keys(source).length === 0) {
      alert('Vui lòng nhập nội dung thuyết minh trước khi tạo TTS');
      return;
    }

    const provider = getActiveProviderName('tts');
    const langList = languages && languages.length ? languages : [{ id: 'vn', name: 'Vietnamese', code: 'VN' }];
    const targets = targetLangId ? langList.filter(l => l.id === targetLangId) : langList.filter(l => l.enabled !== false);
    if (!targets.length) {
      alert('Không có ngôn ngữ để tạo TTS');
      return;
    }

    setCreateAssetVoiceTtsGenerating(true);
    try {
      for (const lang of targets) {
        const text = source[lang.id] || source[(lang.code || '').toLowerCase()] || Object.values(source).find(Boolean) || '';
        if (!text || !text.trim()) {
          showWarning(`Chưa có nội dung cho ${lang.name}`);
          continue;
        }

        const voiceModel = createAssetVoiceModel[lang.id] || 'default';
        const mappedVoice = getVoiceForLanguage(lang.code, 'female', voiceModel);

        const res = await serviceAPI.textToSpeech({
          id_path: idPath || '',
          supplier: provider,
          text,
          voice: mappedVoice,
          format: 'mp3',
          uploadToS3: true
        });

        const fileUrl = res?.data?.file?.url || res?.data?.file_url || res?.uploadInfo?.s3Url || res?.file_url || res?.url || (res?.data && (res.data.fileUrl || res.data.file_url));
        if (fileUrl) {
          setCreateAssetVoiceLinks(prev => ({ ...prev, [lang.id]: fileUrl }));

          // Try to fill audio field in main info tab if it exists
          if (createAssetFormData?.variables) {
            const audioVariable = createAssetFormData.variables.find(v =>
              v.asset_variable_name.toLowerCase().includes('audio') ||
              v.asset_variable_name.toLowerCase().includes('voice')
            );

            if (audioVariable) {
              const inputEl = createAssetFormRef.current?.elements.namedItem(audioVariable.asset_variable_name) as HTMLInputElement | null;
              if (inputEl) {
                inputEl.value = fileUrl;
                // Update preview for the audio field so it shows in the form
                setTtsPreviews(prev => ({ ...prev, [audioVariable.asset_variable_name]: fileUrl }));
                // Also trigger the input event to ensure any listeners are notified
                const event = new Event('input', { bubbles: true });
                inputEl.dispatchEvent(event);
              }
            }
          }
        }
      }
      showSuccess('Tạo TTS thành công');
    } catch (err) {
      console.error('TTS generation error (create asset voice)', err);
      showError('Không thể tạo giọng nói từ TTS', 'Lỗi');
    } finally {
      setCreateAssetVoiceTtsGenerating(false);
    }
  };

  // Add function to handle audio URL changes and show preview
  const handleCreateVoiceAudioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const url = input.value;
    const previewId = `${input.name}_preview`;
    const previewElement = document.getElementById(previewId);

    if (!previewElement) return;

    if (url && (url.includes('.mp3') || url.includes('.wav') || url.includes('.ogg'))) {
      // Show preview
      previewElement.classList.remove('hidden');

      // Update audio source
      const audioElement = previewElement.querySelector('audio') as HTMLAudioElement;
      if (audioElement) {
        audioElement.src = url;
      }

      // Update link
      const linkElement = previewElement.querySelector('a') as HTMLAnchorElement;
      if (linkElement) {
        linkElement.href = url;
      }

      // Update URL display
      const urlDisplay = previewElement.querySelector('.url-display');
      if (urlDisplay) {
        urlDisplay.textContent = url;
      }
    } else {
      // Hide preview if URL is invalid or empty
      previewElement.classList.add('hidden');
    }
  };

  // Handle opening file picker for audio upload
  const handleOpenAudioFilePicker = (inputName: string) => {
    setCurrentAudioInputName(inputName);
    setShowAudioFilePicker(true);
  };

  // Handle file selection from file picker
  const handleAudioFileSelect = (file: S3Object) => {
    if (file.isFolder) return; // Skip folders

    // Check if it's an audio file
    const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac'];
    const isAudioFile = audioExtensions.some(ext =>
      file.name.toLowerCase().endsWith(ext)
    );

    if (!isAudioFile) {
      alert('Vui lòng chọn file audio (.mp3, .wav, .ogg, .m4a, .aac)');
      return;
    }

    // Get full URL - file.key might already contain the path
    let fileUrl = '';
    if (file.key.startsWith('https://')) {
      // If file.key is already full URL
      fileUrl = file.key;
    } else {
      // Build URL from key
      const cleanKey = file.key.startsWith('/') ? file.key : `/${file.key}`;
      fileUrl = `https://s3.hcm-1.cloud.cmctelecom.vn/starglobal-3d${cleanKey}`;
    }

    // Check if it's for voice tab or regular form
    if (currentAudioInputName && editFormVisible) {
      // For voice tab in edit mode
      setEditAssetVoiceLinks(prev => ({ ...prev, [currentAudioInputName]: fileUrl }));
    } else if (currentAudioInputName && createAssetVisible) {
      // For voice tab in create mode
      setCreateAssetVoiceLinks(prev => ({ ...prev, [currentAudioInputName]: fileUrl }));
    } else {
      // For regular form fields (backward compatibility)
      if (createAssetFormRef.current && currentAudioInputName) {
        const inputEl = createAssetFormRef.current.elements.namedItem(currentAudioInputName) as HTMLInputElement | null;
        if (inputEl) {
          inputEl.value = fileUrl;
          // Trigger onChange to show preview
          const event = new Event('input', { bubbles: true });
          inputEl.dispatchEvent(event);
        }
      }

      if (formRef.current && currentAudioInputName) {
        const inputEl = formRef.current.elements.namedItem(currentAudioInputName) as HTMLInputElement | null;
        if (inputEl) {
          inputEl.value = fileUrl;
          // Trigger onChange to show preview
          const event = new Event('input', { bubbles: true });
          inputEl.dispatchEvent(event);
        }
      }
    }

    setShowAudioFilePicker(false);
    setCurrentAudioInputName('');
  };

  // Handle file upload to S3 from file picker
  const handleAudioFileUpload = async (files: File[]) => {
    if (files.length === 0) return;

    setAudioFileUploading(true);
    try {
      const file = files[0];

      // Check if it's an audio file
      const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac'];
      const isAudioFile = audioExtensions.some(ext =>
        file.name.toLowerCase().endsWith(ext)
      );

      if (!isAudioFile) {
        alert('Vui lòng chọn file audio (.mp3, .wav, .ogg, .m4a, .aac)');
        return;
      }

      const uploadResponse = await s3UploadService.uploadToS3(idPath || '', {
        body: { file },
        subfolder: 'audio'
      });

      if (uploadResponse?.success && uploadResponse?.file_url) {
        // Auto-select the uploaded file using the returned URL
        if (createAssetFormRef.current && currentAudioInputName) {
          const inputEl = createAssetFormRef.current.elements.namedItem(currentAudioInputName) as HTMLInputElement | null;
          if (inputEl) {
            inputEl.value = uploadResponse.file_url;
            // Trigger onChange to show preview
            const event = new Event('input', { bubbles: true });
            inputEl.dispatchEvent(event);
          }
        }
        setShowAudioFilePicker(false);
      } else {
        alert('Upload thất bại: ' + (uploadResponse?.error || 'Có lỗi xảy ra'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Có lỗi xảy ra khi upload file');
    } finally {
      setAudioFileUploading(false);
    }
  };

  // Auto-generate voice name when narrations change
  const generateVoiceName = () => {
    if (!createVoiceFormData?.variables || !createAssetFormRef.current) return;

    // Find name field
    const nameVariable = createVoiceFormData.variables.find(v =>
      v.asset_variable_name.toLowerCase().includes('name') ||
      v.asset_variable_name.toLowerCase().includes('ten') ||
      v.title?.toLowerCase().includes('tên')
    );

    // Find title/display name field
    const titleVariable = createVoiceFormData.variables.find(v =>
      v.asset_variable_name.toLowerCase().includes('title') ||
      v.asset_variable_name.toLowerCase().includes('display') ||
      v.title?.toLowerCase().includes('hiển thị') ||
      v.title?.toLowerCase().includes('tiêu đề')
    );

    if (!nameVariable && !titleVariable) {

      return;
    }

    // Get current model from AI context
    const currentModel = getActiveProviderName('tts') || 'google';

    // Get languages that have content
    const languagesWithContent = Object.keys(createVoiceNarrations).filter(langId =>
      createVoiceNarrations[langId]?.trim()
    );

    if (languagesWithContent.length === 0) {
      return;
    }

    // Generate name: Voice + Model + Languages
    const modelName = currentModel.charAt(0).toUpperCase() + currentModel.slice(1);
    const languageNames = languagesWithContent
      .map(langId => {
        // Try to get language name from languages context
        const lang = languages.find(l => l.id === langId);
        if (lang?.name) {
          // Use short form of language name
          if (lang.name.includes('Tiếng Việt')) return 'Việt';
          if (lang.name.includes('English')) return 'Anh';
          return lang.name.split(' ')[0]; // Take first word
        }
        return langId.toUpperCase(); // Fallback to language ID
      })
      .join(', ');

    const generatedName = `Voice ${modelName} ${languageNames}`;

    // Update the name field
    if (nameVariable) {
      const nameInput = createAssetFormRef.current.elements.namedItem(nameVariable.asset_variable_name) as HTMLInputElement | null;
      if (nameInput) {
        nameInput.value = generatedName;
        // Trigger change event to ensure form state is updated
        const event = new Event('change', { bubbles: true });
        nameInput.dispatchEvent(event);
      } else {
        console.log('⚠️ Name input element not found:', nameVariable.asset_variable_name);
      }
    }

    // Update the title/display name field
    if (titleVariable) {
      const titleInput = createAssetFormRef.current.elements.namedItem(titleVariable.asset_variable_name) as HTMLInputElement | null;
      if (titleInput) {
        titleInput.value = generatedName;
        // Trigger change event to ensure form state is updated
        const event = new Event('change', { bubbles: true });
        titleInput.dispatchEvent(event);
      } else {
        console.log('⚠️ Title input element not found:', titleVariable.asset_variable_name);
      }
    }
  };

  // Watch narrations changes to auto-generate name
  useEffect(() => {
    if (createVoiceVisible && Object.keys(createVoiceNarrations).length > 0) {
      // Only auto-generate if both name and title fields are empty
      const nameVariable = createVoiceFormData?.variables?.find(v =>
        v.asset_variable_name.toLowerCase().includes('name') ||
        v.asset_variable_name.toLowerCase().includes('ten') ||
        v.title?.toLowerCase().includes('tên')
      );

      const titleVariable = createVoiceFormData?.variables?.find(v =>
        v.asset_variable_name.toLowerCase().includes('title') ||
        v.asset_variable_name.toLowerCase().includes('display') ||
        v.title?.toLowerCase().includes('hiển thị') ||
        v.title?.toLowerCase().includes('tiêu đề')
      );

      if (createAssetFormRef.current) {
        let shouldGenerate = false;

        // Check if name field is empty
        if (nameVariable) {
          const nameInput = createAssetFormRef.current.elements.namedItem(nameVariable.asset_variable_name) as HTMLInputElement | null;
          if (nameInput && !nameInput.value.trim()) {
            shouldGenerate = true;
          }
        }

        // Check if title field is empty
        if (titleVariable && !shouldGenerate) {
          const titleInput = createAssetFormRef.current.elements.namedItem(titleVariable.asset_variable_name) as HTMLInputElement | null;
          if (titleInput && !titleInput.value.trim()) {
            shouldGenerate = true;
          }
        }

        if (shouldGenerate) {
          generateVoiceName();
        }
      }
    }
  }, [createVoiceNarrations, createVoiceVisible, languages, createVoiceFormData]);

  // Save new voice asset
  const handleSaveNewVoice = async () => {
    if (!createVoiceFormData || !createAssetFormRef.current) return;

    try {
      setCreateVoiceSaving(true);

      // Get the entity_id (model ID) from creatingUnderAssetUuid or navigation history
      const entityId = creatingUnderAssetUuid ?? (
        navigationHistory.length > 0 &&
          navigationHistory[navigationHistory.length - 1]?.parentType === 'asset'
          ? navigationHistory[navigationHistory.length - 1].parentUuid
          : ''
      );

      if (!entityId) {
        alert('Không tìm thấy model để thêm voice. Vui lòng double-click vào một model trước.');
        return;
      }

      // Build voices array from narrations and form data
      const voices: any[] = [];
      const formData = new FormData(createAssetFormRef.current);

      // Get audio path from form (if provided)
      const audioVariable = createVoiceFormData.variables.find(v =>
        v.asset_variable_name.toLowerCase().includes('audio') ||
        v.asset_variable_name.toLowerCase().includes('voice')
      );

      let audioPath = '';
      if (audioVariable) {
        const audioUrl = formData.get(audioVariable.asset_variable_name)?.toString() || '';
        if (audioUrl) {
          // Extract path from S3 URL or use as-is if it's already a path
          if (audioUrl.includes('s3.hcm-1.cloud.cmctelecom.vn')) {
            // Extract the path part after the domain and project ID
            // const urlParts = audioUrl.split(`${idPath}/`);
            audioPath = audioUrl
          } else {
            audioPath = audioUrl.startsWith('/') ? audioUrl.slice(1) : audioUrl;
          }
        } else {
          audioPath = 'upload/audio'; // Default path
        }
      } else {
        audioPath = 'upload/audio'; // Default path
      }

      // Create voice entries for each language with narration content
      Object.entries(createVoiceNarrations).forEach(([languageId, transcript]) => {
        if (transcript && transcript.trim()) {
          voices.push({
            language_id: languageId,
            gender: "nu", // Default to female, could be made configurable
            audio_path: audioPath,
            transcript: transcript.trim()
          });
        }
      });

      if (voices.length === 0) {
        alert('Vui lòng nhập nội dung thuyết minh cho ít nhất một ngôn ngữ');
        return;
      }

      // Prepare API body according to the specified format
      const apiBody = {
        entity_type: "asset",
        entity_id: entityId,
        voices: voices
      };


      // Call the voice service API
      const response = await voiceService.addNewVoices(idPath || '', { body: apiBody });

      if (response?.success || response?.message || !response?.error) {
        alert('Tạo voice thành công!');
        handleCloseVoiceForm();

        // Refresh the current view to show updates
        if (selectedAssetTypeId) {
          const currentLevel = currentData?.requested_level || 0;
          const currentParentUuid = navigationHistory.length > 0
            ? navigationHistory[navigationHistory.length - 1].parentUuid
            : '';
          fetchCategoriesAndAssets(selectedAssetTypeId, currentLevel, currentParentUuid, false);
        }
      } else {
        console.error('Voice creation failed:', response);
        const errorMessage = response?.error?.message || response?.message || 'Có lỗi xảy ra khi tạo voice';
        alert(errorMessage);
      }
    } catch (err) {
      console.error('Error saving new voice:', err);
      alert('Có lỗi xảy ra khi tạo voice');
    } finally {
      setCreateVoiceSaving(false);
    }
  };

  // Close create asset form
  const handleCloseAssetForm = () => {
    setCreateAssetVisible(false);
    setCreateAssetFormData(null);
    setThumbnailUploadState({});
    setDocumentUploadState({});
    setCreatingAssetTypeId(null);
    setCreatingUnderAssetUuid(null);
    // Reset tab states
    setCreateAssetActiveTab('info');
    setCreateAssetVoiceNarrations({});
    setCreateAssetVoiceLinks({});
    setCreateAssetVoiceModel({});
    setCreateAssetVoiceTtsGenerating(false);
    setTtsPreviews({});
    if (createAssetFormRef.current) {
      createAssetFormRef.current.reset();
    }
  };

  // Get file icon based on asset type
  const getAssetIcon = (assetTypeName: string) => {
    switch (assetTypeName.toLowerCase()) {
      case 'video':
        return LargeVideoIcon;
      case 'image':
      case 'photo':
        return LargeImageIcon;
      case 'audio':
        return LargeAudioIcon;
      case 'model':
      case '3d':
        return Large3DIcon;
      case 'document':
        return LargeDocumentIcon;
      case 'flipbook':
        return LargeFlipBookIcon;
      default:
        return LargeDocumentIcon;
    }
  };

  // Get badge color based on asset_type_id
  const getAssetTypeBadgeColor = (assetTypeId: number) => {
    switch (assetTypeId) {
      case 1: return 'bg-purple-100 text-purple-700';  // 3D Model
      case 2: return 'bg-blue-100 text-blue-700';      // Photo
      case 3: return 'bg-orange-100 text-orange-700';  // Document
      case 4: return 'bg-red-100 text-red-700';        // Video
      case 5: return 'bg-green-100 text-green-700';    // Audio
      case 6: return 'bg-pink-100 text-pink-700';      // Flipbook
      case 7: return 'bg-teal-100 text-teal-700';      // Article
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Filter function for search
  const filterItems = (items: (CategoryData | AssetData)[], searchTerm: string) => {
    if (!searchTerm.trim()) return items;

    return items.filter(item => {
      const title = 'category_title' in item
        ? item.category_title
        : (item.asset_title || `Asset ${item.uuid}`);
      return title.toLowerCase().includes(searchTerm.toLowerCase());
    });
  };

  const filteredCategories = currentData?.categories
    ? filterItems(currentData.categories, searchTerm) as CategoryData[]
    : [];

  const filteredAssets = currentData?.assets
    ? filterItems(currentData.assets, searchTerm) as AssetData[]
    : [];

  return (

    <div className="h-full bg-gray-50 z-100 w-full" style={{ position: 'absolute' }}>
      {translationBudgetEstimate && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Xác nhận dịch tự động</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Ước lượng chi phí cho {translationBudgetEstimate.label}
                </p>
              </div>
              <button
                type="button"
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                onClick={() => resolveTranslationBudgetEstimate(false)}
              >
                x
              </button>
            </div>

            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">Số ký tự tính phí</span>
                <span className="font-medium text-gray-900">{translationBudgetEstimate.billableCharacterCount.toLocaleString('vi-VN')}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">Số ngôn ngữ đích</span>
                <span className="font-medium text-gray-900">{translationBudgetEstimate.targetCount}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">Chi phí ước tính</span>
                <span className="font-semibold text-gray-900">{formatEstimateVnd(translationBudgetEstimate.estimatedCostVnd)}</span>
              </div>
              <div className="h-px bg-gray-200" />
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">Ngân sách còn lại</span>
                <span className={`font-semibold ${translationBudgetEstimate.canExecute ? 'text-emerald-700' : 'text-red-600'}`}>
                  {translationBudgetEstimate.hasBudget
                    ? formatEstimateVnd(translationBudgetEstimate.budget?.remaining_budget_vnd)
                    : 'Chưa cấp ngân sách'}
                </span>
              </div>
            </div>

            {!translationBudgetEstimate.canExecute && (
              <p className="mt-3 text-sm text-red-600">
                Ngân sách dịch không đủ để thực hiện thao tác này.
              </p>
            )}

            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => resolveTranslationBudgetEstimate(false)}
              >
                Hủy
              </button>
              <button
                type="button"
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                disabled={!translationBudgetEstimate.canExecute}
                onClick={() => resolveTranslationBudgetEstimate(true)}
              >
                Đồng ý dịch
              </button>
            </div>
          </div>
        </div>
      )}

      {isTranslatingAssets && (
        <div className="absolute inset-0 bg-[#00000099] z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4 shadow-lg">
            <div className="animate-spin h-10 w-10 border-4 border-gray-300 border-t-blue-600 rounded-full"></div>
            <span className="text-gray-700 text-lg font-medium">Đang dịch tài nguyên...</span>
          </div>
        </div>
      )}
      <div className="h-screen overflow-hidden bg-[#F8F8F8] flex">
        {/* Left Sidebar - Asset Types */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          {/* Sidebar Header */}
          <div className="bg-[#ECECEC] px-4 py-6 border-b">
            <h2 className="text-lg font-bold uppercase text-black">
              LOẠI TÀI NGUYÊN
            </h2>
          </div>

          {/* Asset Types List */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading && assetTypes.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
                  <span className="text-gray-600 text-sm">Đang tải loại tài nguyên...</span>
                </div>
              </div>
            ) : error && assetTypes.length === 0 ? (
              <div className="text-red-600 text-center py-8">
                <div className="mb-4">
                  <p className="font-medium">Có lỗi xảy ra</p>
                  <p className="text-sm mt-1">{error}</p>
                </div>
                <button
                  onClick={() => {
                    if (idPath) {
                      window.location.reload();
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
                >
                  Thử lại
                </button>
              </div>
            ) : assetTypes.length === 0 ? (
              <div className="text-gray-600 text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 opacity-20">
                  <FolderIcon />
                </div>
                <p className="font-medium">Chưa có loại tài nguyên</p>
                <p className="text-sm mt-1">Vui lòng kiểm tra cấu hình dự án</p>
              </div>
            ) : (
              <div className="space-y-2">
                {assetTypes.map((assetType) => (
                  <button
                    key={assetType.id}
                    onClick={() => handleAssetTypeSelect(assetType)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${selectedAssetType?.id === assetType.id
                      ? 'bg-blue-100 border border-blue-300 text-blue-800'
                      : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center">
                        {(() => {
                          const IconComponent = getAssetIcon(assetType.name);
                          return <IconComponent />;
                        })()}
                      </div>
                      <span className="font-medium capitalize">{assetType.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Main Header */}
          <div className="bg-[#ECECEC] p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-8">
                <h1 className="text-[28px] font-bold uppercase text-black tracking-wide">
                  QUẢN LÝ TÀI NGUYÊN
                </h1>
              </div>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="flex items-center justify-center w-10 h-10 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                title="Đóng quản lý tài nguyên"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Toolbar and Search */}
          {selectedAssetType && (
            <div className="bg-white border-b-2 border-[#ECECEC] px-6 py-5">
              <div className="flex items-center justify-between">
                {/* Search */}
                <div className="flex items-center gap-4 bg-white border border-[#E0E0E0] rounded-[10px] px-[27px] py-5 w-[480px] h-[60px]">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <SearchIcon />
                  </div>
                  <input
                    type="text"
                    placeholder="Tìm kiếm tài nguyên"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1 text-xl text-[#B0B0B0] placeholder-[#B0B0B0] bg-transparent outline-none"
                  />
                </div>

                {/* Navigation and Action Buttons */}
                <div className="flex items-center gap-4">
                  {/* Create Category Button */}
                  <button
                    onClick={handleCreateCategory}
                    disabled={loading || formLoading || createCategoryLoading}
                    className="flex items-center gap-[10px] px-[15px] py-[10px] bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-[10px] transition-colors"
                  >
                    <div className="w-6 h-6 flex items-center justify-center">
                      <AddIcon />
                    </div>
                    <span className="text-base font-medium">
                      {createCategoryLoading ? 'Đang tải...' : 'Tạo danh mục'}
                    </span>
                  </button>

                  {/* Create Asset Button */}
                  <button
                    onClick={() => handleCreateAsset()}
                    disabled={loading || formLoading || createAssetLoading}
                    className="flex items-center gap-[10px] px-[15px] py-[10px] bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-[10px] transition-colors"
                  >
                    <div className="w-6 h-6 flex items-center justify-center">
                      <AddIcon />
                    </div>
                    <span className="text-base font-medium">
                      {createAssetLoading ? 'Đang tải...' : 'Tạo hiện vật'}
                    </span>
                  </button>

                  {/* Create Voice Button */}
                  {/* <button
                    onClick={() => handleCreateVoice()}
                    disabled={loading || formLoading || createVoiceLoading}
                    className="flex items-center gap-[10px] px-[15px] py-[10px] bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-[10px] transition-colors"
                  >
                    <div className="w-6 h-6 flex items-center justify-center">
                      <AudioIcon />
                    </div>
                    <span className="text-base font-medium">
                      {createVoiceLoading ? 'Đang tải...' : 'Thêm Voice'}
                    </span>
                  </button> */}

                  {/* Back Navigation Button */}
                  <button
                    onClick={handleBackNavigation}
                    disabled={navigationHistory.length === 0}
                    className={`flex items-center gap-[10px] px-[10px] py-[10px] rounded-[10px] transition-colors ${navigationHistory.length === 0
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-gray-50'
                      }`}
                  >
                    <div className="w-10 h-10 flex items-center justify-center">
                      <ArrowLeftIcon />
                    </div>
                    <span className="text-xl text-black font-normal">Quay lại</span>
                  </button>
                </div>
              </div>

              {/* Breadcrumb */}
              <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                {getBreadcrumbPath().map((item, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <span className="mx-2">/</span>}
                    <span className={index === getBreadcrumbPath().length - 1 ? 'font-medium text-black' : ''}>
                      {item}
                    </span>
                  </React.Fragment>
                ))}
              </div>

              {/* File Upload Section */}
              <div className="mt-6">
                <FileUploader
                  selectedAssetType={selectedAssetType}
                  currentCategoryUuid={
                    navigationHistory.length > 0
                      ? navigationHistory[navigationHistory.length - 1].parentUuid
                      : ''
                  }
                  onUploadComplete={() => {
                    // Refresh current data after upload
                    if (selectedAssetTypeId) {
                      const currentLevel = currentData?.requested_level || 0;
                      const currentParentUuid = navigationHistory.length > 0
                        ? navigationHistory[navigationHistory.length - 1].parentUuid
                        : '';
                      fetchCategoriesAndAssets(selectedAssetTypeId, currentLevel, currentParentUuid, false);
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-6">
            {!selectedAssetType ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-24 h-24 mb-6 opacity-20">
                  <FolderIcon />
                </div>
                <h3 className="text-xl font-medium text-gray-500 mb-2">
                  Chọn loại tài nguyên
                </h3>
                <p className="text-base text-gray-400 max-w-md">
                  Chọn một loại tài nguyên từ menu bên trái để xem danh sách danh mục và hiện vật.
                </p>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex items-center gap-3">
                  <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
                  <span className="text-gray-600">Đang tải dữ liệu...</span>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-red-600 text-center">
                  <p className="mb-4">{error}</p>
                  <button
                    onClick={() => {
                      if (selectedAssetTypeId) {
                        const currentLevel = currentData?.requested_level || 0;
                        const currentParentUuid = navigationHistory.length > 0
                          ? navigationHistory[navigationHistory.length - 1].parentUuid
                          : '';
                        fetchCategoriesAndAssets(selectedAssetTypeId, currentLevel, currentParentUuid, false);
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Thử lại
                  </button>
                </div>
              </div>
            ) : currentData ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                {/* Render Categories */}
                {filteredCategories.map((category) => (
                  <div
                    key={category.uuid}
                    onDoubleClick={() => handleCategoryDoubleClick(category)}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer relative"
                  >
                    {/* Action buttons */}
                    <div className="absolute top-2 right-2 flex gap-1 z-10">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveCategory(category);
                        }}
                        className="w-8 h-8 flex items-center justify-center bg-yellow-100 hover:bg-yellow-200 rounded-full"
                        title="Di chuyển thư mục"
                      >
                        <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditCategoryName(category);
                        }}
                        className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full"
                        title="Chỉnh sửa tên danh mục"
                      >
                        <EditIcon />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCategory(category);
                        }}
                        className="w-8 h-8 flex items-center justify-center bg-red-100 hover:bg-red-200 rounded-full"
                        title="Xóa thư mục"
                      >
                        <TrashIcon />
                      </button>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 flex items-center justify-center bg-blue-50 rounded-lg">
                        <FolderIcon />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-medium text-black truncate">
                          {category.category_title}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Danh mục
                        </p>
                        {category.description && (
                          <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                            {category.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <button
                        onClick={() => {
                          if (category.asset_count && category.asset_count > 0) {
                            handleOpenTranslateAssetsEstimate(category);
                          }
                        }}
                        disabled={!category.asset_count || category.asset_count === 0}
                        className={`inline-block mt-4 px-3 py-1 text-xs rounded-full transition-colors ${category.asset_count && category.asset_count > 0
                          ? 'bg-green-100 text-green-600 hover:bg-green-200 cursor-pointer'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                          }`}
                        title={category.asset_count && category.asset_count > 0 ? 'Nhấp để dịch tất cả assets' : 'Không có assets để dịch'}
                      >
                        Dịch thông tin {category.asset_count || 0} tài nguyên
                      </button>
                    </div>

                    {/* Labels/Tags */}
                    {objectLabels[category.uuid] && objectLabels[category.uuid].length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {objectLabels[category.uuid].slice(0, 3).map((label) => (
                          <span
                            key={label.id}
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-white"
                            style={{ backgroundColor: label.color || '#59CE6A' }}
                          >
                            {label.name}
                          </span>
                        ))}
                        {objectLabels[category.uuid].length > 3 && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600">
                            +{objectLabels[category.uuid].length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Render Assets */}
                {filteredAssets.map((asset) => {
                  const IconComponent = getAssetIcon(asset.asset_type_name);
                  const thumbnailUrl = asset.attributes?.thumbnail?.values;
                  const assetLabels = objectLabels[asset.uuid];

                  // Debug log for labels
                  if (assetLabels) {
                    console.log('[Labels-Debug] Asset', asset.uuid, 'has labels:', assetLabels);
                  }

                  return (
                    <div
                      key={asset.uuid}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200"
                      onDoubleClick={() => handleAssetDoubleClick(asset)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-lg overflow-hidden">
                          {thumbnailUrl ? (
                            <img
                              src={thumbnailUrl}
                              alt={asset.asset_title || 'Asset'}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                // Fallback to icon if image fails to load
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                target.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className={thumbnailUrl ? 'hidden' : ''}>
                            <IconComponent />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-medium text-black truncate">
                            {asset.asset_title || `${asset.asset_type_name} ${asset.uuid.slice(-6)}`}
                          </h3>
                          {/* Badge hiển thị loại asset - dựa vào asset.asset_type_id */}
                          <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getAssetTypeBadgeColor(asset.asset_type_id)}`}>
                            {asset.asset_type_name}
                          </span>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`inline-block w-2 h-2 rounded-full ${asset.is_show ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                            <span className="text-xs text-gray-400">
                              {asset.is_show ? 'Hiển thị' : 'Ẩn'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-0.5">
                          {/* Preview Model button - only show for 3D assets */}
                          {asset.attributes?.model3d?.values && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePreviewModel(asset)}
                                className="h-12 w-12 p-0 text-blue-600 hover:text-blue-800"
                                title="Xem trước mô hình 3D"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </Button>
                              <div className="h-6 w-px bg-black"></div>
                            </>
                          )}
                          {/* Add to Cart button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (typeof onAddToCart === 'function') {
                                onAddToCart({
                                  uuid: asset.uuid,
                                  title: asset.asset_title || `Asset ${asset.uuid.slice(-6)}`,
                                  type: asset.asset_type_name,
                                  thumbnail: asset.attributes?.thumbnail?.values,
                                });
                              }
                            }}
                            className="h-12 w-12 p-0 text-green-600 hover:text-green-800"
                            title="Thêm vào giỏ mượn"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </Button>
                          <div className="h-6 w-px bg-black"></div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveAsset(asset)}
                            className="h-12 w-12 p-0 text-yellow-600 hover:text-yellow-800"
                            title="Di chuyển hiện vật"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                            </svg>
                          </Button>
                          <div className="h-6 w-px bg-black"></div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditAsset(asset)}
                            className="h-12 w-12 p-0"
                          >
                            <EditIcon className="h-6 w-6" />
                          </Button>
                          <div className="h-6 w-px bg-black"></div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAsset(asset)}
                            className="h-12 w-12 p-0 text-destructive hover:text-destructive"
                          >
                            <TrashIcon className="h-6 w-6" />
                          </Button>
                        </div>
                      </div>

                      {/* Labels/Tags */}
                      {objectLabels[asset.uuid] && objectLabels[asset.uuid].length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {objectLabels[asset.uuid].slice(0, 3).map((label) => (
                            <span
                              key={label.id}
                              className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-white"
                              style={{ backgroundColor: label.color || '#59CE6A' }}
                            >
                              {label.name}
                            </span>
                          ))}
                          {objectLabels[asset.uuid].length > 3 && (
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600">
                              +{objectLabels[asset.uuid].length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Asset attributes preview */}
                      {asset.attributes && Object.keys(asset.attributes).length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="text-xs text-gray-500">
                            {Object.entries(asset.attributes)
                              .filter(([key]) => !['thumbnail', 'parent_display_id'].includes(key))
                              .slice(0, 2)
                              .map(([key, attr]) => (
                                <div key={key} className="truncate">
                                  <span className="font-medium">{attr.title}:</span> {attr.values}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Empty state */}
                {filteredCategories.length === 0 && filteredAssets.length === 0 && (
                  <div className="col-span-3 flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-24 h-24 mb-6 opacity-20">
                      <FolderIcon />
                    </div>
                    <h3 className="text-xl font-medium text-gray-500 mb-2">
                      {searchTerm ? 'Không tìm thấy kết quả' : 'Không có dữ liệu'}
                    </h3>
                    <p className="text-base text-gray-400 max-w-md">
                      {searchTerm
                        ? `Không tìm thấy tài nguyên nào khớp với "${searchTerm}"`
                        : 'Chưa có category hoặc asset nào trong mục này.'
                      }
                    </p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {/* Edit Asset Form Modal */}
        {editFormVisible && editAsset && formAssetData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-full h-full flex flex-col overflow-hidden">
              {/* Modal Header */}
              <div className="bg-[#ECECEC] px-6 py-4 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-black">
                    CHỈNH SỬA ASSET - {formAssetData.assetType.name.toUpperCase()}
                  </h2>
                  <button
                    onClick={handleCloseEditForm}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Tab Navigation */}
                <div className="mt-4 flex space-x-1 bg-gray-100 p-1 rounded-lg">
                  <button
                    onClick={() => setEditAssetActiveTab('info')}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${editAssetActiveTab === 'info'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    Thông tin Asset
                  </button>
                  <button
                    onClick={() => setEditAssetActiveTab('voice')}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${editAssetActiveTab === 'voice'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    Voice & Thuyết minh
                  </button>
                  <button
                    onClick={() => setEditAssetActiveTab('history')}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${editAssetActiveTab === 'history'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    Lịch sử mượn
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto flex-1 min-h-0">
                {editAssetActiveTab === 'info' ? (
                  <>
                    {/* Asset Info Tab Content */}
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                      <h3 className="font-medium text-blue-800 mb-2">Thông tin Asset</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium">UUID:</span> {editAsset.uuid}
                        </div>
                        <div>
                          <span className="font-medium">Tên:</span> {editAsset.asset_title || 'Không có tên'}
                        </div>
                        <div>
                          <span className="font-medium">Loại:</span> {editAsset.asset_type_name}
                        </div>
                        <div>
                          <span className="font-medium">Trạng thái:</span> {editAsset.is_show ? 'Hiển thị' : 'Ẩn'}
                        </div>
                      </div>
                    </div>

                    {/* Asset Form Fields */}
                    <form ref={formRef}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {formAssetData.variables
                          .filter(variable => variable.title || variable.asset_variable_name) // Only show variables with titles
                          .sort((a, b) => (a.index || 999) - (b.index || 999)) // Sort by index
                          .map((variable) => {

                            const currentValue = editAsset.attributes?.[variable.asset_variable_name]?.values || '';
                            const thumbnailState = thumbnailUploadState[variable.asset_variable_name];
                            const thumbnailPreview = thumbnailState?.previewUrl || currentValue;
                            return (
                              <div key={variable.id} className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  {variable.title}
                                  {variable.description && (
                                    <span className="text-gray-500 text-xs block">
                                      {variable.description}
                                    </span>
                                  )}
                                </label>

                                {/* Render different input types based on variable name */}
                                {variable.asset_variable_name.includes('thumbnail') ? (
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="url"
                                        name={variable.asset_variable_name}
                                        defaultValue={currentValue}
                                        placeholder="URL ảnh đại diện. Khuyến nghị dưới 1MB"
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                      <label className="relative inline-flex cursor-pointer items-center rounded-md border border-dashed border-blue-500 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50">
                                        <span className="pointer-events-none">
                                          {thumbnailState?.uploading ? 'Đang tải...' : 'Upload'}
                                        </span>
                                        <input
                                          type="file"
                                          accept="image/*"
                                          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                          onChange={(e) => handleThumbnailFileChange(variable.asset_variable_name, e)}
                                          disabled={thumbnailState?.uploading}
                                        />
                                      </label>
                                    </div>
                                    {thumbnailPreview && (
                                      <div className="w-20 h-20 border rounded overflow-hidden">
                                        <img
                                          src={thumbnailPreview}
                                          alt="Preview"
                                          className="w-full h-full object-cover"
                                          onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.style.display = 'none';
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ) : variable.asset_variable_name.includes('model3d') ? (
                                  <input
                                    type="url"
                                    name={variable.asset_variable_name}
                                    defaultValue={currentValue}
                                    placeholder="URL file model 3D"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                ) : variable.asset_variable_name.includes('video') ? (
                                  <input
                                    type="url"
                                    name={variable.asset_variable_name}
                                    defaultValue={currentValue}
                                    placeholder="URL video"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                ) : variable.asset_variable_name.includes('audio') || variable.asset_variable_name.includes('voice') ? (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="url"
                                        name={variable.asset_variable_name}
                                        defaultValue={currentValue}
                                        placeholder="URL audio/voice hoặc paste link từ TTS"
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        onChange={(e) => {
                                          // Auto preview when URL is entered
                                          const url = e.target.value;
                                          if (url && (url.includes('.mp3') || url.includes('.wav') || url.includes('.m4a') || url.includes('audio') || url.startsWith('http'))) {
                                            setTtsPreviews(prev => ({ ...prev, [variable.asset_variable_name]: url }));
                                          } else if (!url) {
                                            // Remove preview if URL is cleared
                                            setTtsPreviews(prev => {
                                              const newPreviews = { ...prev };
                                              delete newPreviews[variable.asset_variable_name];
                                              return newPreviews;
                                            });
                                          }
                                        }}
                                      />
                                    </div>

                                    {/* Audio Preview - show existing value or newly entered URL */}
                                    {(currentValue || ttsPreviews[variable.asset_variable_name]) && (
                                      <div className="bg-gray-50 p-3 rounded-lg">
                                        <div className="flex items-center gap-3 mb-2">
                                          <span className="text-sm font-medium text-gray-700">Preview Audio:</span>
                                          <a
                                            href={ttsPreviews[variable.asset_variable_name] || currentValue}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                                            title="Mở link trong tab mới"
                                          >
                                            🔗 Link
                                          </a>
                                        </div>
                                        <audio
                                          controls
                                          src={ttsPreviews[variable.asset_variable_name] || currentValue}
                                          className="w-full h-8"
                                          preload="metadata"
                                        />
                                        <div className="mt-2 text-xs text-gray-600 break-all">
                                          URL: {ttsPreviews[variable.asset_variable_name] || currentValue}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : variable.asset_variable_name.includes('document') || variable.asset_variable_name.includes('tai_lieu') || variable.asset_variable_name.includes('file') ? (
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="url"
                                        name={variable.asset_variable_name}
                                        value={documentUploadState[variable.asset_variable_name]?.previewUrl || currentValue || ''}
                                        onChange={(e) => {
                                          // Allow manual URL input
                                          setDocumentUploadState(prev => ({
                                            ...prev,
                                            [variable.asset_variable_name]: {
                                              ...prev[variable.asset_variable_name],
                                              previewUrl: e.target.value,
                                              uploading: false
                                            }
                                          }));
                                        }}
                                        placeholder="URL tài liệu (PDF, Word, Excel...)"
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                      <label className="relative inline-flex cursor-pointer items-center rounded-md border border-dashed border-green-500 px-3 py-2 text-sm text-green-600 hover:bg-green-50">
                                        <span className="pointer-events-none">
                                          {documentUploadState[variable.asset_variable_name]?.uploading ? 'Đang tải...' : 'Upload'}
                                        </span>
                                        <input
                                          type="file"
                                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf"
                                          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                          onChange={(e) => handleDocumentFileChange(variable.asset_variable_name, e)}
                                          disabled={documentUploadState[variable.asset_variable_name]?.uploading}
                                        />
                                      </label>
                                    </div>
                                    {/* Document Preview/Info */}
                                    {(currentValue || documentUploadState[variable.asset_variable_name]?.previewUrl) && (
                                      <div className="bg-green-50 p-3 rounded-lg">
                                        <div className="flex items-center gap-3">
                                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-green-800 truncate">
                                              {documentUploadState[variable.asset_variable_name]?.fileName || 'Tài liệu'}
                                            </p>
                                            <button
                                              onClick={() => handlePdfPreview(
                                                documentUploadState[variable.asset_variable_name]?.previewUrl || currentValue || '',
                                                documentUploadState[variable.asset_variable_name]?.fileName || 'Tài liệu'
                                              )}
                                              className="text-xs text-green-600 hover:text-green-800 underline"
                                            >
                                              Xem tài liệu
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : variable.asset_variable_name.includes('showhide') ? (
                                  <select
                                    name={variable.asset_variable_name}
                                    defaultValue={currentValue ?? '1'}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="1">Hiển thị</option>
                                    <option value="0">Ẩn</option>
                                  </select>
                                ) : variable.asset_variable_name.includes('mieu_ta') ? (
                                  <div className="w-full">
                                    <CKEditor
                                      data={currentValue}
                                      onChange={(data) => {
                                        // Update the form value when editor changes
                                        const input = formRef.current?.querySelector(`[name="${variable.asset_variable_name}"]`) as HTMLInputElement;
                                        if (input) {
                                          input.value = data;
                                        }
                                      }}
                                      placeholder={`Nhập ${variable.title?.toLowerCase()}`}
                                      height="350px"
                                    />
                                    {/* Hidden input to store the data for form submission */}
                                    <input
                                      type="hidden"
                                      name={variable.asset_variable_name}
                                      defaultValue={currentValue}
                                    />
                                  </div>
                                ) : (
                                  <input
                                    type="text"
                                    name={variable.asset_variable_name}
                                    defaultValue={currentValue}
                                    placeholder={`Nhập ${variable.title?.toLowerCase()}`}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </form>
                  </>
                ) : (
                  <>
                    {/* Voice Tab Content */}
                    <div className="space-y-6">
                      <div className="mb-4 p-4 bg-purple-50 rounded-lg">
                        <h3 className="font-medium text-purple-800 mb-2">Voice & Thuyết minh cho Asset</h3>
                        <p className="text-sm text-purple-700">
                          Tạo và quản lý voice cho asset theo từng ngôn ngữ. Voice sẽ được phát khi tương tác với asset.
                        </p>
                      </div>

                      {/* Language Voice Forms */}
                      {languages?.filter(lang => lang.enabled !== false)?.map(language => (
                        <div key={language.id} className="border border-gray-200 rounded-lg p-4">
                          <h4 className="font-medium text-gray-800 mb-4 flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center font-bold">
                              {language.code?.toUpperCase() || language.id?.toUpperCase()}
                            </span>
                            {language.name}
                          </h4>

                          <div className="space-y-4">
                            {/* Narration Content */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nội dung thuyết minh
                              </label>
                              <div className="flex gap-2 mb-2">
                                <button
                                  type="button"
                                  onClick={() => translateAllFields(editAssetVoiceNarrations, setEditAssetVoiceNarrations, 'nội dung thuyết minh', false, false)}
                                  disabled={isTranslating}
                                  className="px-3 py-1 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 disabled:opacity-50"
                                >
                                  {isTranslating ? 'Đang dịch...' : 'Dịch tự động tất cả'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => translateSingleField(editAssetVoiceNarrations, setEditAssetVoiceNarrations, language.id, 'narration', 'nội dung thuyết minh')}
                                  disabled={isTranslating}
                                  className="px-3 py-1 text-xs bg-green-100 text-green-600 rounded hover:bg-green-200 disabled:opacity-50"
                                >
                                  {isTranslating ? 'Đang dịch...' : `Dịch sang ${language.name}`}
                                </button>
                              </div>
                              <textarea
                                value={editAssetVoiceNarrations[language.id] || ''}
                                onChange={(e) => setEditAssetVoiceNarrations(prev => ({ ...prev, [language.id]: e.target.value }))}
                                placeholder={`Nhập nội dung thuyết minh cho ${language.name}`}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>

                            {/* Voice Link and Upload */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Link Voice
                              </label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="url"
                                  value={editAssetVoiceLinks[language.id] || ''}
                                  onChange={(e) => setEditAssetVoiceLinks(prev => ({ ...prev, [language.id]: e.target.value }))}
                                  placeholder="URL tệp âm thanh"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCurrentAudioInputName(language.id);
                                    setShowAudioFilePicker(true);
                                  }}
                                  className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
                                >
                                  Tải lên
                                </button>
                              </div>

                              {/* Audio Preview */}
                              {editAssetVoiceLinks[language.id] && (
                                <div className="mt-2 bg-gray-50 p-3 rounded-lg">
                                  <audio
                                    controls
                                    src={editAssetVoiceLinks[language.id]}
                                    className="w-full h-8"
                                    preload="metadata"
                                  />
                                </div>
                              )}
                            </div>


                            {/* Voice Model Selection */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Giọng nói
                              </label>
                              <select
                                value={editAssetVoiceModel[language.id] || 'default'}
                                onChange={(e) => setEditAssetVoiceModel(prev => ({ ...prev, [language.id]: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="default">Mặc định</option>
                                {(() => {
                                  const langCode = language.id === 'vn' || language.id === 'vi-vn' ? 'vi' : (language.code?.toLowerCase() || language.id?.toLowerCase());
                                  let code = langCode;
                                  if (code === 'vn' || code === 'vi-vn') code = 'vi';
                                  else if (code.startsWith('en')) code = 'en';
                                  else if (code.startsWith('zh') || code.startsWith('cmn')) code = 'zh';
                                  else if (code.startsWith('es')) code = 'es';
                                  else if (code.startsWith('de')) code = 'de';

                                  const voiceGroups = AVAILABLE_VOICE_CODES[code];
                                  const voices = voiceGroups ? [...(voiceGroups.female || []), ...(voiceGroups.male || [])] : [];
                                  return voices.map(v => (
                                    <option key={v.code} value={v.code}>{v.name} ({v.level})</option>
                                  ));
                                })()}
                              </select>
                            </div>

                            {/* TTS Generation */}
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => generateEditAssetVoiceTts({ targetLangId: language.id })}
                                disabled={editAssetVoiceTtsGenerating || !editAssetVoiceNarrations[language.id]?.trim()}
                                className="px-4 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {editAssetVoiceTtsGenerating ? 'Đang tạo...' : `Tạo voice thuyết minh cho ${language.name}`}
                              </button>
                              <button
                                type="button"
                                onClick={() => generateEditAssetVoiceTts()}
                                disabled={editAssetVoiceTtsGenerating || Object.keys(editAssetVoiceNarrations).length === 0}
                                className="px-4 py-2 bg-purple-800 text-white text-sm rounded-md hover:bg-purple-900 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {editAssetVoiceTtsGenerating ? 'Đang tạo...' : 'Tạo voice thuyết minh tất cả'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Save Voice Only Button */}
                      <div className="mt-6 p-4 bg-purple-50 rounded-lg border-t">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-purple-800 mb-1">Lưu Voice riêng biệt</h4>
                            <p className="text-sm text-purple-600">
                              Lưu chỉ voice data mà không cần lưu thông tin asset khác
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleSaveAsset()}
                            disabled={saveLoading || Object.keys(editAssetVoiceNarrations).every(langId => !editAssetVoiceNarrations[langId]?.trim())}
                            className="px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {saveLoading && (
                              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                            )}
                            <span>{saveLoading ? 'Đang lưu...' : 'Lưu Voice'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Narrations Section for Audio Assets in Edit Mode - Keep for backward compatibility */}
                {editAssetActiveTab === 'info' && editAsset && formAssetData && formAssetData.assetType.name.toLowerCase().includes('audio') && (
                  <div className="mt-6 border-t pt-6">
                    <div className="mb-4 p-4 bg-purple-50 rounded-lg">
                      <h3 className="font-medium text-purple-800 mb-2">Nội dung thuyết minh (TTS)</h3>
                      <p className="text-sm text-purple-700">Nhập nội dung thuyết minh cho từng ngôn ngữ và tạo file âm thanh.</p>
                    </div>

                    <DynamicLanguageForm
                      id_path={idPath || ''}
                      titles={editNarrations}
                      onTitleChange={(langCode, value) => setEditNarrations(prev => ({ ...prev, [langCode]: value }))}
                      onAutoTranslate={async () => { await translateAllFields(editNarrations, setEditNarrations, 'nội dung thuyết minh', false, false); }}
                      onTranslateSingle={async (langId) => { await translateSingleField(editNarrations, setEditNarrations, langId, 'narration', 'nội dung thuyết minh'); }}
                      onGenerateSingleTTS={(langId) => generateEditTts({ targetLangId: langId })}
                      onGenerateAllTTS={() => generateEditTts()}
                      isTtsGenerating={ttsGenerating}
                      ttsPreviewUrls={ttsPreviews}
                      isTranslating={isTranslating}
                      labelPrefix="Thuyết minh"
                    />
                  </div>
                )}

                {/* Borrow History Tab */}
                {editAssetActiveTab === 'history' && editAsset && (
                  <div className="mt-4">
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                      <h3 className="font-medium text-blue-800 mb-2">Lịch sử mượn hiện vật</h3>
                      <p className="text-sm text-blue-700">Xem lịch sử các lần mượn và trả của hiện vật này.</p>
                    </div>
                    <AssetBorrowHistory assetUuid={editAsset.uuid} />
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
                <button
                  onClick={handleCloseEditForm}
                  disabled={saveLoading}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Hủy
                </button>
                <button
                  onClick={handleSaveAsset}
                  disabled={saveLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saveLoading && (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  )}
                  <span>{saveLoading ? 'Đang lưu...' : 'Lưu thay đổi'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Category Form Modal */}
        {createCategoryVisible && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-full h-full flex flex-col overflow-hidden">
              {/* Modal Header */}
              <div className="bg-[#ECECEC] px-6 py-4 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-black">
                    TẠO CATEGORY MỚI - {selectedAssetType?.name.toUpperCase()}
                  </h2>
                  <button
                    onClick={handleCloseCategoryForm}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto flex-1 min-h-0">
                <form ref={createCategoryFormRef}>
                  <div className="space-y-6">
                    {/* Category Titles for Each Language */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-gray-700 mb-3">Tên Category theo ngôn ngữ <span className="text-red-500">*</span></h3>
                          <p className="text-xs text-gray-500 mb-4">Vui lòng nhập tên category bằng tiếng Việt (bắt buộc) và các ngôn ngữ khác</p>
                        </div>
                        <button
                          type="button"
                          onClick={handleTranslateAllNewCategoryTitles}
                          disabled={isTranslating}
                          className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 whitespace-nowrap text-sm"
                        >
                          {isTranslating ? (
                            <>
                              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                              <span>Đang dịch...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.05 5.05a7 7 0 10-9.9 9.9M12 15v2m4.95-2.05a7 7 0 001.4-13.8" />
                              </svg>
                              <span>Dịch tất cả</span>
                            </>
                          )}
                        </button>
                      </div>
                      {languages?.filter(lang => lang.enabled !== false)?.map((lang) => (
                        <div key={lang.id} className="space-y-1">
                          <label className="block text-sm font-medium text-gray-700">
                            {lang.name} {lang.id === 'vn' && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="text"
                            name={`category_title_${lang.id}`}
                            placeholder={`Nhập tên category bằng ${lang.name}`}
                            value={newCategoryTitles[lang.id] || ''}
                            onChange={(e) => setNewCategoryTitles(prev => ({
                              ...prev,
                              [lang.id]: e.target.value
                            }))}
                            required={lang.id === 'vn'}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                      ))}
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Mô tả <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="description"
                        placeholder="Nhập mô tả"
                        value={newCategoryDescription}
                        onChange={(e) => setNewCategoryDescription(e.target.value)}
                        rows={3}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>

                    {/* Asset Type Selection - Di chuyển category */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Phân loại hiển thị (Tab)
                      </label>
                      <select
                        name="asset_type_id"
                        defaultValue=""
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                      >
                        {CATEGORY_ASSET_TYPE_OPTIONS.map(opt => (
                          <option key={opt.value ?? 'all'} value={opt.value ?? ''}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500">
                        Chọn tab để hiển thị folder này. Các asset bên trong không bị ảnh hưởng.
                      </p>
                    </div>

                    {/* Parent Category Info */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-medium text-blue-800 mb-2">Vị trí tạo category</h3>
                      <div className="text-sm text-blue-700">
                        <strong>Parent category:</strong>{' '}
                        {navigationHistory.length > 0 && currentData?.parent_category
                          ? currentData.parent_category.category_title
                          : 'Root (cấp gốc)'
                        }
                      </div>
                      <div className="text-sm text-blue-700 mt-1">
                        <strong>Đường dẫn:</strong> {getBreadcrumbPath().join(' / ')}
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
                <button
                  onClick={handleCloseCategoryForm}
                  disabled={createCategoryLoading}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Hủy
                </button>
                <button
                  onClick={handleSaveCategory}
                  disabled={createCategoryLoading || !newCategoryDescription.trim()}
                  title={!newCategoryDescription.trim() ? 'Vui lòng nhập mô tả' : ''}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {createCategoryLoading && (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  )}
                  <span>{createCategoryLoading ? 'Đang tạo...' : 'Tạo Category'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Category Form Modal */}
        {editCategoryVisible && editCategory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-full h-full flex flex-col overflow-hidden">
              {/* Modal Header */}
              <div className="bg-[#ECECEC] px-6 py-4 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-black">
                    CHỈNH SỬA CATEGORY - {selectedAssetType?.name.toUpperCase()}
                  </h2>
                  <button
                    onClick={handleCloseEditCategoryForm}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto flex-1 min-h-0">
                <form ref={editCategoryFormRef}>
                  <div className="space-y-6">
                    {/* Category Titles for Each Language */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-gray-700 mb-3">Tên Category theo ngôn ngữ <span className="text-red-500">*</span></h3>
                          <p className="text-xs text-gray-500 mb-4">Vui lòng nhập tên category bằng tiếng Việt (bắt buộc) và các ngôn ngữ khác</p>
                        </div>
                        <button
                          type="button"
                          onClick={handleTranslateAllCategoryTitles}
                          disabled={isTranslating}
                          className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 whitespace-nowrap text-sm"
                        >
                          {isTranslating ? (
                            <>
                              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                              <span>Đang dịch...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.05 5.05a7 7 0 10-9.9 9.9M12 15v2m4.95-2.05a7 7 0 001.4-13.8" />
                              </svg>
                              <span>Dịch tất cả</span>
                            </>
                          )}
                        </button>
                      </div>
                      {languages?.filter(lang => lang.enabled !== false)?.map((lang) => {
                        const langCode = lang.id;
                        // Get translation for this language, or empty string if not available
                        const translation = editCategoryData?.translations?.[langCode];
                        const defaultValue = translation?.title || '';
                        const currentValue = editedCategoryTitles[langCode] !== undefined ? editedCategoryTitles[langCode] : defaultValue;

                        return (
                          <div key={lang.id} className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">
                              {lang.name} {lang.id === 'vn' && <span className="text-red-500">*</span>}
                            </label>
                            <input
                              type="text"
                              name={`category_title_${lang.id}`}
                              placeholder={`Nhập tên category bằng ${lang.name}`}
                              value={currentValue}
                              onChange={(e) => setEditedCategoryTitles(prev => ({
                                ...prev,
                                [lang.id]: e.target.value
                              }))}
                              required={lang.id === 'vn'}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        );
                      })}
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Mô tả <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="description"
                        placeholder="Nhập mô tả"
                        value={editedCategoryDescription}
                        onChange={(e) => setEditedCategoryDescription(e.target.value)}
                        rows={3}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Asset Type Selection - Di chuyển category */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Di chuyển sang Tab khác
                      </label>
                      <select
                        name="asset_type_id"
                        defaultValue={editCategory.asset_type_id ?? ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        {CATEGORY_ASSET_TYPE_OPTIONS.map(opt => (
                          <option key={opt.value ?? 'all'} value={opt.value ?? ''}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500">
                        ⚠️ Chỉ thay đổi vị trí hiển thị của folder. Các asset bên trong <strong>KHÔNG</strong> bị ảnh hưởng.
                      </p>
                      {editCategory.asset_type_id && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs text-gray-600">Hiện tại:</span>
                          <span className={`text-xs px-2 py-1 rounded-full bg-${CATEGORY_ASSET_TYPE_OPTIONS.find(o => o.value === editCategory.asset_type_id)?.color || 'gray'}-100 text-${CATEGORY_ASSET_TYPE_OPTIONS.find(o => o.value === editCategory.asset_type_id)?.color || 'gray'}-700`}>
                            {CATEGORY_ASSET_TYPE_OPTIONS.find(o => o.value === editCategory.asset_type_id)?.label || 'Không xác định'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Category Info */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-medium text-blue-800 mb-2">Thông tin Category</h3>
                      <div className="text-sm text-blue-700 space-y-1">
                        <div>
                          <strong>UUID:</strong> {editCategory?.uuid}
                        </div>
                        <div>
                          <strong>Parent category:</strong>{' '}
                          {navigationHistory.length > 0 && currentData?.parent_category
                            ? currentData.parent_category.category_title
                            : 'Root (cấp gốc)'
                          }
                        </div>
                        <div>
                          <strong>Đường dẫn:</strong> {getBreadcrumbPath().join(' / ')}
                        </div>
                        <div>
                          <strong>Trạng thái:</strong> {editCategory?.is_show ? 'Hiển thị' : 'Ẩn'}
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
                <button
                  onClick={handleCloseEditCategoryForm}
                  disabled={editCategoryLoading}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Hủy
                </button>
                <button
                  onClick={handleSaveCategoryName}
                  disabled={editCategoryLoading || !editedCategoryDescription.trim()}
                  title={!editedCategoryDescription.trim() ? 'Vui lòng nhập mô tả' : ''}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {editCategoryLoading && (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  )}
                  <span>{editCategoryLoading ? 'Đang cập nhật...' : 'Cập nhật Category'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Asset Form Modal */}
        {createAssetVisible && createAssetFormData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-full h-full flex flex-col overflow-hidden">
              {/* Modal Header */}
              <div className="bg-[#ECECEC] px-6 py-4 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <h2 className="text-xl font-bold text-black">
                      TẠO ASSET MỚI - {createAssetFormData.assetType.name.toUpperCase()}
                    </h2>
                    {/* Allow choosing different asset type when creating under a model/folder */}
                    <div className="ml-4">
                      <label className="text-sm text-gray-600 mr-2">Chọn loại:</label>
                      <select
                        value={(creatingAssetTypeId ?? selectedAssetType?.id ?? '') as any}
                        onChange={async (e) => {
                          try {
                            setCreateAssetLoading(true);
                            const newTypeId = parseInt(e.target.value);
                            if (newTypeId !== creatingAssetTypeId) {
                              setCreatingAssetTypeId(newTypeId);
                              // Reload form data for the new asset type
                              const response = await assetServices.getFormAssetByType({
                                id_path: idPath || '',
                                type: String(newTypeId)
                              });
                              if (response?.data) {
                                setCreateAssetFormData(response.data);
                              }
                            }
                          } catch (error) {
                            console.error('Error changing asset type:', error);
                          } finally {
                            setCreateAssetLoading(false);
                          }
                        }}
                        className="px-2 py-1 border rounded-md"
                      >
                        {(assetTypes || []).map(t => (
                          <option value={t.id} key={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseAssetForm}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Tab Navigation */}
                <div className="mt-4 flex space-x-1 bg-gray-100 p-1 rounded-lg">
                  <button
                    onClick={() => setCreateAssetActiveTab('info')}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${createAssetActiveTab === 'info'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    Thông tin Asset
                  </button>
                  <button
                    onClick={() => setCreateAssetActiveTab('voice')}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${createAssetActiveTab === 'voice'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    Voice & Thuyết minh
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto flex-1 min-h-0">
                {/* Category Info */}
                <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-medium text-blue-800 mb-2">Vị trí tạo asset</h3>
                  <div className="text-sm text-blue-700">
                    <div>
                      <strong>Asset type:</strong> {selectedAssetType.name}
                    </div>
                    <div>
                      <strong>Category:</strong>{' '}
                      {navigationHistory.length > 0 && currentData?.parent_category
                        ? currentData.parent_category.category_title
                        : 'Root (cấp gốc)'
                      }
                    </div>
                    <div className="text-sm text-blue-700 mt-1">
                      <strong>Đường dẫn:</strong> {getBreadcrumbPath().join(' / ')}
                    </div>
                  </div>
                </div>

                {/* Asset Form Fields */}
                <form ref={createAssetFormRef}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {createAssetFormData.variables
                      .filter(variable => variable.title || variable.asset_variable_name) // Only show variables with titles
                      .sort((a, b) => (a.index || 999) - (b.index || 999)) // Sort by index
                      .map((variable) => {
                        const currentValue = '';
                        const thumbnailState = thumbnailUploadState[variable.asset_variable_name];
                        const thumbnailPreview = thumbnailState?.previewUrl || '';
                        return (
                          <div key={variable.id} className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              {variable.title}
                              {variable.description && (
                                <span className="text-gray-500 text-xs block">
                                  {variable.description}
                                </span>
                              )}
                            </label>

                            {variable.asset_variable_name.includes('thumbnail') ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="url"
                                    name={variable.asset_variable_name}
                                    value={thumbnailState?.previewUrl || ''}
                                    onChange={(e) => {
                                      setThumbnailUploadState(prev => ({
                                        ...prev,
                                        [variable.asset_variable_name]: {
                                          ...prev[variable.asset_variable_name],
                                          previewUrl: e.target.value,
                                          uploading: false
                                        }
                                      }));
                                    }}
                                    placeholder="URL ảnh đại diện. Khuyến nghị dưới 1MB"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <label className="relative inline-flex cursor-pointer items-center rounded-md border border-dashed border-blue-500 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50">
                                    <span className="pointer-events-none">
                                      {thumbnailState?.uploading ? 'Đang tải...' : 'Tải ảnh lên'}
                                    </span>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                      onChange={(e) => handleThumbnailFileChange(variable.asset_variable_name, e)}
                                      disabled={thumbnailState?.uploading}
                                    />
                                  </label>
                                </div>
                                {thumbnailPreview && (
                                  <div className="w-20 h-20 border rounded overflow-hidden">
                                    <img
                                      src={thumbnailPreview}
                                      alt="Preview"
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            ) : variable.asset_variable_name.includes('model3d') ? (
                              <input
                                type="url"
                                name={variable.asset_variable_name}
                                placeholder="URL file model 3D"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : variable.asset_variable_name.includes('video') ? (
                              <input
                                type="url"
                                name={variable.asset_variable_name}
                                placeholder="URL video"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : variable.asset_variable_name.includes('audio') || variable.asset_variable_name.includes('voice') ? (
                              <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="url"
                                    name={variable.asset_variable_name}
                                    placeholder="URL audio/voice hoặc paste link từ TTS"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    onChange={(e) => {
                                      // Auto preview when URL is entered
                                      const url = e.target.value;
                                      if (url && (url.includes('.mp3') || url.includes('.wav') || url.includes('.m4a') || url.includes('audio'))) {
                                        setTtsPreviews(prev => ({ ...prev, [variable.asset_variable_name]: url }));
                                      }
                                    }}
                                  />
                                </div>

                                {/* Audio Preview */}
                                {ttsPreviews[variable.asset_variable_name] && (
                                  <div className="bg-gray-50 p-3 rounded-lg">
                                    <div className="flex items-center gap-3 mb-2">
                                      <span className="text-sm font-medium text-gray-700">Preview Audio:</span>
                                      <a
                                        href={ttsPreviews[variable.asset_variable_name]}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                                        title="Mở link trong tab mới"
                                      >
                                        🔗 Link
                                      </a>
                                    </div>
                                    <audio
                                      controls
                                      src={ttsPreviews[variable.asset_variable_name]}
                                      className="w-full h-8"
                                      preload="metadata"
                                    />
                                  </div>
                                )}
                              </div>
                            ) : variable.asset_variable_name.includes('document') || variable.asset_variable_name.includes('tai_lieu') || variable.asset_variable_name.includes('file') ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="url"
                                    name={variable.asset_variable_name}
                                    value={documentUploadState[variable.asset_variable_name]?.previewUrl || ''}
                                    onChange={(e) => {
                                      setDocumentUploadState(prev => ({
                                        ...prev,
                                        [variable.asset_variable_name]: {
                                          ...prev[variable.asset_variable_name],
                                          previewUrl: e.target.value,
                                          uploading: false
                                        }
                                      }));
                                    }}
                                    placeholder="URL tài liệu (PDF, Word, Excel...)"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <label className="relative inline-flex cursor-pointer items-center rounded-md border border-dashed border-green-500 px-3 py-2 text-sm text-green-600 hover:bg-green-50">
                                    <span className="pointer-events-none">
                                      {documentUploadState[variable.asset_variable_name]?.uploading ? 'Đang tải...' : 'Tải tệp lên'}
                                    </span>
                                    <input
                                      type="file"
                                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf"
                                      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                      onChange={(e) => handleDocumentFileChange(variable.asset_variable_name, e)}
                                      disabled={documentUploadState[variable.asset_variable_name]?.uploading}
                                    />
                                  </label>
                                </div>
                                {/* Document Preview/Info */}
                                {documentUploadState[variable.asset_variable_name]?.previewUrl && (
                                  <div className="bg-green-50 p-3 rounded-lg">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-green-800 truncate">
                                          {documentUploadState[variable.asset_variable_name]?.fileName || 'Tài liệu đã upload'}
                                        </p>
                                        <button
                                          onClick={() => handlePdfPreview(
                                            documentUploadState[variable.asset_variable_name].previewUrl || '',
                                            documentUploadState[variable.asset_variable_name]?.fileName || 'Tài liệu'
                                          )}
                                          className="text-xs text-green-600 hover:text-green-800 underline"
                                        >
                                          Xem tài liệu
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : variable.asset_variable_name.includes('showhide') ? (
                              <select
                                name={variable.asset_variable_name}
                                defaultValue={currentValue ?? '1'}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="1">Hiển thị</option>
                                <option value="0">Ẩn</option>
                              </select>
                            ) : variable.asset_variable_name.includes('mieu_ta') ? (
                              <textarea
                                name={variable.asset_variable_name}
                                placeholder={`Nhập ${variable.title?.toLowerCase()}`}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <input
                                type="text"
                                name={variable.asset_variable_name}
                                placeholder={`Nhập ${variable.title?.toLowerCase()}`}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            )}
                          </div>
                        );
                      })}
                  </div>
                </form>

                {/* Narrations Section for Audio Assets */}
                {createAssetFormData.assetType.name.toLowerCase().includes('audio') && (
                  <div className="mt-6 border-t pt-6">
                    <div className="mb-4 p-4 bg-purple-50 rounded-lg">
                      <h3 className="font-medium text-purple-800 mb-2">Nội dung thuyết minh (TTS)</h3>
                      <p className="text-sm text-purple-700">Nhập nội dung thuyết minh cho từng ngôn ngữ và tạo file âm thanh.</p>
                    </div>

                    <DynamicLanguageForm
                      id_path={idPath || ''}
                      titles={createNarrations}
                      onTitleChange={(langCode, value) => setCreateNarrations(prev => ({ ...prev, [langCode]: value }))}
                      onAutoTranslate={async () => { await translateAllFields(createNarrations, setCreateNarrations, 'nội dung thuyết minh', false, false); }}
                      onTranslateSingle={async (langId) => { await translateSingleField(createNarrations, setCreateNarrations, langId, 'narration', 'nội dung thuyết minh'); }}
                      onGenerateSingleTTS={(langId) => generateTtsForLanguages({ targetLangId: langId })}
                      onGenerateAllTTS={() => generateTtsForLanguages()}
                      isTtsGenerating={ttsGenerating}
                      ttsPreviewUrls={ttsPreviews}
                      isTranslating={isTranslating}
                      labelPrefix="Thuyết minh"
                    />
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
                <button
                  onClick={handleCloseAssetForm}
                  disabled={createAssetLoading}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Hủy
                </button>
                <button
                  onClick={handleSaveNewAsset}
                  disabled={createAssetLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {createAssetLoading && (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  )}
                  <span>{createAssetLoading ? 'Đang tạo...' : 'Tạo Asset'}</span>
                </button>
              </div>
            </div>
          </div>

        )}

        {/* Create Voice Form Modal */}
        {createVoiceVisible && createVoiceFormData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-full h-full flex flex-col overflow-hidden">
              <div className="bg-[#ECECEC] px-6 py-4 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-black">TẠO VOICE - {createVoiceFormData.assetType?.name?.toUpperCase()}</h2>
                  <button onClick={handleCloseVoiceForm} className="text-gray-500 hover:text-gray-700">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto flex-1 min-h-0">
                {/* Voice Asset Info Form */}
                <div className="mb-6 p-4 bg-green-50 rounded-lg">
                  <h3 className="font-medium text-green-800 mb-4">Thông tin Voice Asset</h3>

                  <form ref={createAssetFormRef}>
                    <div className="grid grid-cols-1 gap-4">
                      {createVoiceFormData.variables
                        .filter(variable => variable.title || variable.asset_variable_name)
                        .sort((a, b) => (a.index || 999) - (b.index || 999))
                        .map((variable) => (
                          <div key={variable.id} className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              {variable.title}
                              {variable.description && (
                                <span className="text-gray-500 text-xs block">
                                  {variable.description}
                                </span>
                              )}
                            </label>

                            {variable.asset_variable_name.includes('audio') || variable.asset_variable_name.includes('voice') ? (
                              <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="url"
                                    name={variable.asset_variable_name}
                                    placeholder="URL audio/voice sẽ được tự động điền khi tạo TTS"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                    onChange={handleCreateVoiceAudioChange}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleOpenAudioFilePicker(variable.asset_variable_name)}
                                    disabled={audioFileUploading}
                                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Chọn file audio từ S3 hoặc upload mới"
                                  >
                                    {audioFileUploading ? (
                                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                    ) : (
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                      </svg>
                                    )}
                                    {audioFileUploading ? 'Uploading...' : 'Upload'}
                                  </button>
                                </div>

                                {/* Show audio preview if URL exists */}
                                <div id={`${variable.asset_variable_name}_preview`} className="hidden bg-gray-50 p-3 rounded-lg">
                                  <div className="flex items-center gap-3 mb-2">
                                    <span className="text-sm font-medium text-gray-700">Audio Preview:</span>
                                    <a href="#" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 underline">
                                      🔗 Link
                                    </a>
                                  </div>
                                  <audio controls className="w-full h-8" preload="metadata" />
                                  <div className="mt-2 text-xs text-gray-600 break-all">
                                    URL: <span className="url-display"></span>
                                  </div>
                                </div>
                              </div>
                            ) : variable.asset_variable_name.includes('mieu_ta') ? (
                              <textarea
                                name={variable.asset_variable_name}
                                placeholder={`Nhập ${variable.title?.toLowerCase()}`}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                              />
                            ) : variable.asset_variable_name.toLowerCase().includes('name') ||
                              variable.asset_variable_name.toLowerCase().includes('ten') ||
                              variable.title?.toLowerCase().includes('tên') ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  name={variable.asset_variable_name}
                                  placeholder={`Nhập ${variable.title?.toLowerCase()}`}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                                <button
                                  type="button"
                                  onClick={generateVoiceName}
                                  className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center gap-2"
                                  title="Tự động tạo tên từ Model + Ngôn ngữ"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  Auto
                                </button>
                              </div>
                            ) : (
                              <input
                                type="text"
                                name={variable.asset_variable_name}
                                placeholder={`Nhập ${variable.title?.toLowerCase()}`}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                              />
                            )}
                          </div>
                        ))}
                    </div>
                  </form>
                </div>

                {/* Narration Content Section */}
                <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-medium text-blue-800 mb-2">Nội dung thuyết minh</h3>
                  <p className="text-sm text-blue-700">Nhập nội dung cho từng ngôn ngữ và tạo TTS.</p>
                </div>

                <DynamicLanguageForm
                  id_path={idPath || ''}
                  titles={createVoiceNarrations}
                  onTitleChange={(langCode, value) => setCreateVoiceNarrations(prev => ({ ...prev, [langCode]: value }))}
                  onAutoTranslate={async () => { await translateAllFields(createVoiceNarrations, setCreateVoiceNarrations, 'nội dung thuyết minh', false, false); }}
                  onTranslateSingle={async (langId) => { await translateSingleField(createVoiceNarrations, setCreateVoiceNarrations, langId, 'narration', 'nội dung thuyết minh'); }}
                  onGenerateSingleTTS={(langId) => generateCreateVoiceTts({ targetLangId: langId })}
                  onGenerateAllTTS={() => generateCreateVoiceTts()}
                  isTtsGenerating={createVoiceTtsGenerating}
                  ttsPreviewUrls={createVoiceTtsPreviews}
                  isTranslating={isTranslating}
                />

                {/* Voice Preview Section - hiển thị link và audio player */}
                <div className="mt-6 space-y-4">
                  <h4 className="font-medium text-gray-800 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5 17h4l6 6V1L9 7H5v10z" />
                    </svg>
                    Tệp âm thanh đã tạo
                  </h4>

                  {Object.entries(createVoiceTtsPreviews).map(([langId, url]) => (
                    <div key={langId} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="text-sm font-medium text-gray-800">
                          {languages.find(l => l.id === langId)?.name || langId}
                        </div>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors"
                          title="Mở liên kết trong tab mới"
                        >
                          🔗 Mở liên kết
                        </a>
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(url)}
                          className="text-xs bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700 transition-colors"
                          title="Sao chép liên kết"
                        >
                          📋 Sao chép
                        </button>
                      </div>

                      {/* Audio Player */}
                      <audio controls src={url} className="w-full h-10 mb-3" />

                      {/* Link Display */}
                      <div className="bg-white p-2 rounded border">
                        <div className="text-xs text-gray-600 mb-1">URL:</div>
                        <div className="text-xs text-blue-600 break-all font-mono bg-blue-50 p-1 rounded">
                          {url}
                        </div>
                      </div>
                    </div>
                  ))}

                  {Object.keys(createVoiceTtsPreviews).length === 0 && (
                    <div className="text-center text-gray-500 py-4">
                      Chưa có tệp âm thanh được tạo. Hãy nhập nội dung và bấm nút TTS.
                    </div>
                  )}
                </div>

              </div>

              <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
                <button onClick={handleCloseVoiceForm} disabled={createVoiceSaving} className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">Hủy</button>
                <button onClick={handleSaveNewVoice} disabled={createVoiceSaving} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">{createVoiceSaving && (<div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>)}<span>{createVoiceSaving ? 'Đang tạo...' : 'Tạo âm thanh'}</span></button>
              </div>
            </div>
          </div>
        )}

        {/* Audio File Picker Modal */}
        {showAudioFilePicker && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-4xl h-3/4 flex flex-col">
              <div className="flex justify-between items-center p-6 border-b">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Chọn file Audio</h2>
                  <p className="text-sm text-gray-500 mt-1">Hỗ trợ: .mp3, .wav, .ogg, .m4a, .aac</p>
                </div>
                <button
                  onClick={() => setShowAudioFilePicker(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 p-6 overflow-auto">
                <AudioFileManager
                  namepath={namePath || ''}
                  onFileSelect={handleAudioFileSelect}
                  onUpload={handleAudioFileUpload}
                />
              </div>

              <div className="bg-gray-50 px-6 py-4 border-t flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Chọn file audio (.mp3, .wav, .ogg, .m4a, .aac) hoặc upload file mới
                </div>
                <button
                  onClick={() => setShowAudioFilePicker(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Move Modal */}
        {showMoveModal && moveItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-full flex flex-col overflow-hidden">
              {/* Modal Header */}
              <div className="bg-[#ECECEC] px-6 py-4 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-black">
                    DI CHUYỂN {moveItem.type === 'category' ? 'THƯ MỤC' : 'ASSET'}
                  </h2>
                  <button
                    onClick={handleCloseMoveModal}
                    disabled={moveLoading}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto flex-1 min-h-0">
                {/* Current item info */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-medium text-blue-800 mb-2">
                    Đang di chuyển {moveItem.type === 'category' ? 'thư mục' : 'asset'}
                  </h3>
                  <div className="text-sm text-blue-700">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded">
                        {moveItem.type === 'category' ? (
                          <FolderIcon />
                        ) : (
                          <div className="w-4 h-4 bg-blue-600 rounded"></div>
                        )}
                      </div>
                      <div>
                        <div className="font-medium">{moveItem.title}</div>
                        <div className="text-xs text-blue-600">
                          ID: {moveItem.id.slice(-8)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Target selection */}
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-3 text-sm font-medium text-gray-700">
                      <label>Chọn thư mục đích:</label>
                      <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                        <span>Mẹo: Double click để vào thư mục con</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded-md border border-gray-100 flex-wrap">
                      <button
                        type="button"
                        onClick={handleMoveNavBack}
                        disabled={moveNavHistory.length === 0}
                        className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Quay lại"
                      >
                        <ArrowLeftIcon />
                      </button>
                      <div className="flex items-center gap-1 text-xs text-gray-600 flex-wrap">
                        <span
                          className="font-medium cursor-pointer hover:underline text-blue-600"
                          onClick={() => {
                            setMoveNavHistory([]);
                            loadMoveCategories(0, '');
                          }}
                        >Root</span>
                        {moveNavHistory.map((h, i) => (
                          <React.Fragment key={i}>
                            <ArrowRightIcon />
                            <span
                              className="max-w-[120px] truncate cursor-pointer hover:underline text-blue-600"
                              title={h.title}
                              onClick={() => {
                                const newHistory = moveNavHistory.slice(0, i);
                                setMoveNavHistory(newHistory);
                                loadMoveCategories(h.level, h.parentUuid);
                              }}
                            >{h.title}</span>
                          </React.Fragment>
                        ))}
                        {moveParentUuid !== '' && (
                          <>
                            <ArrowRightIcon />
                            <span className="font-bold text-gray-800 max-w-[120px] truncate">
                              {moveCategories.length > 0 && moveParentUuid !== ''
                                // The actual current category title is hard to get unless we keep track of it
                                // We can get it from the last history item's destination which is the current
                                ? 'Thư mục hiện tại'
                                : '...'
                              }
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {moveCategoriesLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="flex items-center gap-3">
                          <div className="animate-spin h-5 w-5 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
                          <span className="text-gray-600">Đang tải danh sách thư mục...</span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-white">
                        {/* Option to move to current folder */}
                        <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${moveTargetCategory === moveParentUuid ? 'bg-blue-50 border-blue-200 border' : 'hover:bg-gray-50 bg-gray-50 border border-transparent border-dashed'}`}>
                          <input
                            type="radio"
                            name="moveTarget"
                            value={moveParentUuid}
                            checked={moveTargetCategory === moveParentUuid}
                            onChange={(e) => setMoveTargetCategory(e.target.value)}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 flex items-center justify-center rounded ${moveTargetCategory === moveParentUuid ? 'bg-blue-100' : 'bg-gray-200'}`}>
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                              </svg>
                            </div>
                            <span className={`font-medium ${moveTargetCategory === moveParentUuid ? 'text-blue-700' : 'text-gray-700'}`}>
                              {moveParentUuid === '' ? 'Gốc (Root)' : 'Di chuyển vào thư mục này'}
                            </span>
                          </div>
                        </label>

                        {/* Available sub-categories */}
                        {moveCategories
                          .filter(cat => cat.uuid !== moveItem.id) // Don't show current item as option
                          .map((category) => (
                            <label
                              key={category.uuid}
                              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors group ${moveTargetCategory === category.uuid ? 'bg-blue-50 border-blue-200 border' : 'hover:bg-gray-50 border border-transparent'}`}
                              onDoubleClick={() => handleMoveCategoryDoubleClick(category)}
                            >
                              <input
                                type="radio"
                                name="moveTarget"
                                value={category.uuid}
                                checked={moveTargetCategory === category.uuid}
                                onChange={(e) => setMoveTargetCategory(e.target.value)}
                                className="text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex items-center gap-2 flex-1">
                                <div className={`w-6 h-6 flex items-center justify-center rounded ${moveTargetCategory === category.uuid ? 'bg-blue-100' : 'bg-blue-50'}`}>
                                  <FolderIcon />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <span className={`font-medium truncate ${moveTargetCategory === category.uuid ? 'text-blue-700' : 'text-gray-700'}`}>
                                      {category.category_title}
                                    </span>
                                    <span className="text-[10px] text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity">Double-click để mở</span>
                                  </div>
                                  {category.description && (
                                    <p className="text-xs text-gray-500">
                                      {category.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </label>
                          ))}

                        {moveCategories.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <p>Không có thư mục nào để di chuyển đến.</p>
                            <p className="text-sm mt-1">Chỉ có thể di chuyển vào thư mục gốc.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Preview */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Xem trước:</h4>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{moveItem.title}</span>
                      {' → '}
                      <span className="font-medium text-blue-600">
                        {moveTargetCategory === ''
                          ? 'Root'
                          : moveCategories.find(cat => cat.uuid === moveTargetCategory)?.category_title || 'Không xác định'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
                <button
                  onClick={handleCloseMoveModal}
                  disabled={moveLoading}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Hủy
                </button>
                <button
                  onClick={executeMove}
                  disabled={moveLoading || moveCategoriesLoading}
                  className="px-6 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {moveLoading && (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  )}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                  </svg>
                  <span>{moveLoading ? 'Đang di chuyển...' : 'Di chuyển'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Model Preview Modal */}
        {showModelPreview && (
          <ModelPreviewModal
            visible={showModelPreview}
            onClose={() => {
              setShowModelPreview(false);
              // Reload data after saving to see updated attributes
              if (currentData) {
                handleAssetTypeSelect(selectedAssetType!);
              }
            }}
            modelUrl={previewModelUrl}
            assetTypeId={previewAssetTypeId}
            assetUuid={previewAssetUuid}
            materialType={previewMaterialType}
            currentSettings={previewCurrentSettings}
          />
        )}

        {/* PDF Preview Modal */}
        {showPdfPreview && (
          <MediaPreviewModal
            visible={showPdfPreview}
            onClose={() => {
              setShowPdfPreview(false);
              // Clean up blob URL to prevent memory leaks
              if (pdfPreviewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(pdfPreviewUrl);
              }
            }}
            url={pdfPreviewUrl}
            fileType="pdf"
            fileName={pdfPreviewFileName}
          />
        )}

      </div>
    </div>

  );
}
